// 🎮 Ultimate Domino Game Experience - Modern, Engaging, and Awesome!
// Built with game design best practices for maximum engagement
import { GAME_MODE_RULES, getGameRules, getHowToPlayText } from './game/GameModeRules';
import { PlayerProfileManager } from './core/PlayerProfile';
import { ProfileDashboard } from './components/ProfileDashboard';
import { AuthManager } from './services/AuthManager';
export class AwesomeDominoGame {
    canvas;
    ctx;
    gameMode = 'classic';
    difficulty = 'easy'; // Default to easy as requested
    profileManager;
    profileDashboard;
    authManager;
    // Game State
    tiles = [];
    playerHand = [];
    aiHand = [];
    ai2Hand = [];
    ai3Hand = [];
    board = [];
    boardLeftEnd = -1;
    boardRightEnd = -1;
    currentPlayer = 'player';
    currentPlayerIndex = 0; // 0=player, 1=AI1, 2=AI2, 3=AI3
    playerCount = 2;
    gameStarted = false;
    isPaused = false;
    // Multi-player & Team State
    teamScores = { team1: 0, team2: 0 };
    sixLoveStreak = { team1: 0, team2: 0 };
    roundNumber = 1;
    consecutivePasses = 0;
    aiNames = ['Sofia', 'Marcus', 'Elena'];
    aiAvatars = ['🤖', '🎭', '👾'];
    // Scoring & Stats
    playerScore = 0;
    aiScore = 0;
    ai2Score = 0; // Third player score (cutthroat)
    combo = 0;
    maxCombo = 0;
    movesCount = 0;
    timeElapsed = 0;
    gameStartTime = 0;
    // Visual Effects
    particles = [];
    screenShake = 0;
    backgroundHue = 200;
    pulseAnimation = 0;
    transitionAlpha = 0;
    // Interaction
    hoveredTile = null;
    draggedTile = null;
    dragOffset = { x: 0, y: 0 };
    validDropZone = null;
    dropZoneGlow = 0;
    // Power-ups & Features
    powerUps = [];
    hints = [];
    showHint = false;
    achievements = [];
    unlockedFeatures = new Set();
    failedAttempts = 0;
    nudgeShownThisTurn = false;
    lastMoveTime = 0;
    showTutorial = true;
    tutorialStep = 0;
    // Audio Context (for sound effects)
    audioContext = null;
    soundEnabled = true;
    musicVolume = 0.3;
    sfxVolume = 0.5;
    // Animation & Timing
    animationFrame = 0;
    lastFrameTime = 0;
    deltaTime = 0;
    prefersReducedMotion = false;
    pendingAITimeout = 0;
    // Responsive Design
    scale = 1;
    offsetX = 0;
    offsetY = 0;
    constructor() {
        this.init();
    }
    init() {
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.setupDOM();
        this.setupCanvas();
        this.setupEventListeners();
        this.initAudio();
        this.initAchievements();
        this.initProfile();
        this.showMainMenu();
    }
    initProfile() {
        this.profileManager = PlayerProfileManager.getInstance();
        this.profileDashboard = new ProfileDashboard();
        this.authManager = AuthManager.getInstance();
        // Auto-login as guest if no session
        if (!this.authManager.isLoggedIn()) {
            this.authManager.loginAsGuest(this.profileManager.getUsername());
        }
        // Restore last preferences
        const savedMode = this.authManager.getPreference('lastMode', 'classic');
        if (savedMode && GAME_MODE_RULES[savedMode]) {
            this.gameMode = savedMode;
        }
        const savedDifficulty = this.authManager.getPreference('lastDifficulty', 'easy');
        if (savedDifficulty) {
            this.difficulty = savedDifficulty;
        }
    }
    setupDOM() {
        document.body.innerHTML = '';
        document.body.style.cssText = `
      margin: 0;
      padding: 0;
      overflow: hidden;
      background: #0a0a0a;
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
      cursor: default;
      user-select: none;
    `;
        // Add custom styles for smooth animations
        const style = document.createElement('style');
        style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.95); }
        to { opacity: 1; transform: scale(1); }
      }

      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }

      @keyframes glow {
        0%, 100% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.5); }
        50% { box-shadow: 0 0 40px rgba(0, 255, 255, 0.8); }
      }

      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }

      @keyframes tilePlacement {
        0% { transform: scale(1.3) rotate(180deg); opacity: 0.5; }
        50% { transform: scale(1.1) rotate(90deg); }
        100% { transform: scale(1) rotate(0deg); opacity: 1; }
      }

      @keyframes slideInFromTop {
        from { transform: translateY(-100px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      @keyframes fadeInOut {
        0% { opacity: 0; }
        20% { opacity: 1; }
        80% { opacity: 1; }
        100% { opacity: 0; }
      }

      @keyframes scorePopup {
        0% { transform: translate(-50%, -50%) scale(0); opacity: 1; }
        50% { transform: translate(-50%, -50%) scale(1.5); }
        100% { transform: translate(-50%, -100px) scale(1); opacity: 0; }
      }

      @keyframes slideDown {
        from { transform: translateX(-50%) translateY(-50px); opacity: 0; }
        to { transform: translateX(-50%) translateY(0); opacity: 1; }
      }

      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }

      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }

      .menu-button {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        color: white;
        padding: 15px 40px;
        font-size: 18px;
        border-radius: 30px;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        margin: 10px;
        min-height: 48px;
        min-width: 48px;
      }

      .menu-button:hover, .menu-button:focus-visible {
        transform: translateY(-3px);
        box-shadow: 0 7px 25px rgba(102, 126, 234, 0.6);
      }

      .menu-button:focus-visible {
        outline: 3px solid #ffd700;
        outline-offset: 2px;
      }

      .game-mode-card:focus-visible {
        outline: 3px solid #ffd700;
        outline-offset: 2px;
      }

      .achievement-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
        color: #333;
        padding: 15px 25px;
        border-radius: 15px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        animation: slideInRight 0.5s ease-out;
        z-index: 1000;
      }

      @keyframes slideInRight {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .combo-indicator {
        position: fixed;
        top: 80px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 48px;
        font-weight: bold;
        color: #fff;
        text-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
        animation: comboPopup 0.5s ease-out;
        z-index: 500;
      }

      @keyframes comboPopup {
        0% {
          transform: translateX(-50%) scale(0);
          opacity: 0;
        }
        50% {
          transform: translateX(-50%) scale(1.2);
        }
        100% {
          transform: translateX(-50%) scale(1);
          opacity: 1;
        }
      }
    `;
        document.head.appendChild(style);
    }
    setupCanvas() {
        this.canvas = document.createElement('canvas');
        this.canvas.id = 'gameCanvas';
        this.canvas.style.cssText = `
      width: 100vw;
      height: 100vh;
      display: block;
      cursor: pointer;
      touch-action: none;
      -webkit-touch-callout: none;
    `;
        document.body.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');
        this.resizeCanvas();
        // Start animation loop
        this.animate();
    }
    resizeCanvas() {
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = window.innerWidth * dpr;
        this.canvas.height = window.innerHeight * dpr;
        this.ctx.scale(dpr, dpr);
        // Calculate responsive scale
        this.scale = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
    }
    setupEventListeners() {
        // Window events
        window.addEventListener('resize', () => this.resizeCanvas());
        // Mouse events
        this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        this.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        this.canvas.addEventListener('mouseup', (e) => this.handleMouseUp(e));
        this.canvas.addEventListener('wheel', (e) => this.handleWheel(e));
        // Touch events
        this.canvas.addEventListener('touchstart', (e) => this.handleTouchStart(e));
        this.canvas.addEventListener('touchmove', (e) => this.handleTouchMove(e));
        this.canvas.addEventListener('touchend', (e) => this.handleTouchEnd(e));
        // Keyboard events
        document.addEventListener('keydown', (e) => this.handleKeyDown(e));
        document.addEventListener('keyup', (e) => this.handleKeyUp(e));
    }
    initAudio() {
        // Defer AudioContext creation to first user interaction (required by iOS Safari)
        const createContext = () => {
            if (this.audioContext)
                return;
            try {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            catch {
                // Web Audio API not supported
            }
            document.removeEventListener('touchstart', createContext);
            document.removeEventListener('click', createContext);
        };
        document.addEventListener('touchstart', createContext, { once: true });
        document.addEventListener('click', createContext, { once: true });
    }
    playSound(type) {
        if (!this.audioContext || !this.soundEnabled)
            return;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        switch (type) {
            case 'click':
                oscillator.frequency.value = 800;
                gainNode.gain.value = 0.1 * this.sfxVolume;
                oscillator.type = 'sine';
                break;
            case 'place':
                oscillator.frequency.value = 400;
                gainNode.gain.value = 0.2 * this.sfxVolume;
                oscillator.type = 'triangle';
                break;
            case 'win':
                oscillator.frequency.value = 523.25; // C5
                gainNode.gain.value = 0.3 * this.sfxVolume;
                oscillator.type = 'sine';
                // Add harmonics for richer sound
                setTimeout(() => this.playSound('combo'), 100);
                setTimeout(() => this.playSound('powerup'), 200);
                break;
            case 'combo':
                oscillator.frequency.value = 659.25; // E5
                gainNode.gain.value = 0.2 * this.sfxVolume;
                oscillator.type = 'sine';
                break;
            case 'powerup':
                oscillator.frequency.value = 1046.5; // C6
                gainNode.gain.value = 0.15 * this.sfxVolume;
                oscillator.type = 'square';
                break;
            case 'error':
                oscillator.frequency.value = 200;
                gainNode.gain.value = 0.1 * this.sfxVolume;
                oscillator.type = 'sawtooth';
                break;
            case 'slam':
                // Deep, impactful slam sound
                oscillator.frequency.value = 80; // Low frequency for impact
                gainNode.gain.value = 0.4 * this.sfxVolume;
                oscillator.type = 'sawtooth';
                // Add a second impact for depth
                setTimeout(() => {
                    const osc2 = this.audioContext.createOscillator();
                    const gain2 = this.audioContext.createGain();
                    osc2.connect(gain2);
                    gain2.connect(this.audioContext.destination);
                    osc2.frequency.value = 120;
                    gain2.gain.value = 0.3 * this.sfxVolume;
                    osc2.type = 'square';
                    osc2.start(this.audioContext.currentTime);
                    osc2.stop(this.audioContext.currentTime + 0.15);
                }, 20);
                break;
        }
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.1);
        // Fade out
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
    }
    initAchievements() {
        this.achievements = [
            {
                id: 'first-win',
                title: 'First Victory!',
                description: 'Win your first game',
                icon: '🏆',
                unlocked: false,
                progress: 0,
                maxProgress: 1
            },
            {
                id: 'combo-master',
                title: 'Combo Master',
                description: 'Achieve a 5x combo',
                icon: '⚡',
                unlocked: false,
                progress: 0,
                maxProgress: 5
            },
            {
                id: 'speed-demon',
                title: 'Speed Demon',
                description: 'Win a game in under 2 minutes',
                icon: '🚀',
                unlocked: false,
                progress: 0,
                maxProgress: 120
            },
            {
                id: 'perfect-game',
                title: 'Perfect Game',
                description: 'Win without letting AI score',
                icon: '💎',
                unlocked: false,
                progress: 0,
                maxProgress: 1
            },
            {
                id: 'domino-master',
                title: 'Domino Master',
                description: 'Win 10 games',
                icon: '👑',
                unlocked: false,
                progress: 0,
                maxProgress: 10
            }
        ];
    }
    showMainMenu() {
        // Make the game instance available globally for button clicks
        window.game = this;
        // Check if first-time user needs onboarding
        const hasOnboarded = this.profileManager.getProfile()?.stats?.gamesPlayed > 0;
        let onboardedBefore = false;
        try {
            onboardedBefore = localStorage.getItem('dominauts_onboarded') === '1';
        }
        catch { }
        if (!hasOnboarded && !onboardedBefore) {
            this.showOnboarding();
            return;
        }
        this.renderMainMenu();
    }
    showOnboarding() {
        const overlay = document.createElement('div');
        overlay.id = 'onboarding';
        overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      z-index: 2000; display: flex; align-items: center; justify-content: center;
    `;
        overlay.innerHTML = `
      <div style="
        background: rgba(0,0,0,0.4); backdrop-filter: blur(20px);
        border-radius: 24px; padding: 40px; max-width: 500px; width: 90%;
        text-align: center; border: 1px solid rgba(255,255,255,0.2);
      ">
        <div style="font-size: 64px; margin-bottom: 16px;">🎲</div>
        <h1 style="color: white; font-size: 28px; margin-bottom: 8px;">Welcome to Dominauts!</h1>
        <p style="color: rgba(255,255,255,0.8); font-size: 16px; margin-bottom: 32px; line-height: 1.5;">
          How familiar are you with dominoes? We'll set up the perfect game for you.
        </p>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button id="ob-beginner" style="
            background: rgba(255,255,255,0.12); border: 2px solid rgba(255,255,255,0.25);
            color: white; padding: 16px 24px; border-radius: 16px; cursor: pointer;
            font-size: 16px; text-align: left; transition: all 0.2s;
            display: flex; align-items: center; gap: 14px;
          ">
            <span style="font-size: 32px;">🌱</span>
            <div>
              <div style="font-weight: 600;">I'm new to dominoes</div>
              <div style="opacity: 0.7; font-size: 13px; margin-top: 2px;">Start with Classic mode &amp; Easy difficulty</div>
            </div>
          </button>

          <button id="ob-intermediate" style="
            background: rgba(255,255,255,0.12); border: 2px solid rgba(255,255,255,0.25);
            color: white; padding: 16px 24px; border-radius: 16px; cursor: pointer;
            font-size: 16px; text-align: left; transition: all 0.2s;
            display: flex; align-items: center; gap: 14px;
          ">
            <span style="font-size: 32px;">🎯</span>
            <div>
              <div style="font-weight: 600;">I know the basics</div>
              <div style="opacity: 0.7; font-size: 13px; margin-top: 2px;">Try All Fives mode &amp; Medium difficulty</div>
            </div>
          </button>

          <button id="ob-expert" style="
            background: rgba(255,255,255,0.12); border: 2px solid rgba(255,255,255,0.25);
            color: white; padding: 16px 24px; border-radius: 16px; cursor: pointer;
            font-size: 16px; text-align: left; transition: all 0.2s;
            display: flex; align-items: center; gap: 14px;
          ">
            <span style="font-size: 32px;">🔥</span>
            <div>
              <div style="font-weight: 600;">I'm experienced</div>
              <div style="opacity: 0.7; font-size: 13px; margin-top: 2px;">Jump into Cutthroat &amp; Hard difficulty</div>
            </div>
          </button>
        </div>

        <button id="ob-skip" style="
          background: none; border: none; color: rgba(255,255,255,0.6);
          cursor: pointer; font-size: 14px; margin-top: 20px; padding: 8px;
        ">Skip - let me browse all modes</button>
      </div>
    `;
        document.body.appendChild(overlay);
        const applyOnboarding = (mode, diff) => {
            this.gameMode = mode;
            this.difficulty = diff;
            try {
                localStorage.setItem('dominauts_onboarded', '1');
            }
            catch { }
            overlay.remove();
            this.renderMainMenu(mode);
        };
        // Hover effects
        overlay.querySelectorAll('button[id^="ob-"]').forEach(btn => {
            if (btn.id === 'ob-skip')
                return;
            btn.addEventListener('mouseenter', () => {
                btn.style.background = 'rgba(255,255,255,0.2)';
                btn.style.borderColor = 'rgba(255,255,255,0.5)';
            });
            btn.addEventListener('mouseleave', () => {
                btn.style.background = 'rgba(255,255,255,0.12)';
                btn.style.borderColor = 'rgba(255,255,255,0.25)';
            });
        });
        document.getElementById('ob-beginner').onclick = () => applyOnboarding('classic', 'easy');
        document.getElementById('ob-intermediate').onclick = () => applyOnboarding('allfives', 'medium');
        document.getElementById('ob-expert').onclick = () => applyOnboarding('cutthroat', 'hard');
        document.getElementById('ob-skip').onclick = () => {
            try {
                localStorage.setItem('dominauts_onboarded', '1');
            }
            catch { }
            overlay.remove();
            this.renderMainMenu();
        };
    }
    renderMainMenu(preselectedMode) {
        const avatarIcon = this.profileManager.getAvatarIcon();
        const username = this.profileManager.getUsername();
        const level = this.profileManager.getLevel();
        const menu = document.createElement('div');
        menu.id = 'mainMenu';
        menu.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      display: flex; flex-direction: column; align-items: center;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      z-index: 1000; overflow-y: auto; padding: 20px;
    `;
        const modeCardStyle = `
      background: rgba(255,255,255,0.1);
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 16px; padding: 16px; text-align: center;
      cursor: pointer; transition: all 0.3s; backdrop-filter: blur(10px);
      position: relative;
    `;
        const modes = [
            { id: 'classic', icon: '🎯', name: 'Classic', desc: 'Traditional', badge: 'POPULAR' },
            { id: 'allfives', icon: '💯', name: 'All Fives', desc: 'Score 5s', badge: '' },
            { id: 'draw', icon: '⚡', name: 'Draw', desc: 'Fast-paced', badge: '' },
            { id: 'block', icon: '🚫', name: 'Block', desc: 'No drawing', badge: '' },
            { id: 'cutthroat', icon: '🎭', name: 'Cutthroat', desc: '3 players', badge: '' },
            { id: 'partner', icon: '🤝', name: 'Partner', desc: '2v2 teams', badge: '' },
            { id: 'sixlove', icon: '🇯🇲', name: 'Six-Love', desc: 'Win 6 straight', badge: '' },
            { id: 'cross', icon: '✚', name: 'Cross', desc: '4-way layout', badge: '' },
        ];
        const modeCards = modes.map(m => `
      <div class="game-mode-card" data-mode="${m.id}" tabindex="0" role="button"
           aria-label="${m.name} - ${m.desc}" style="${modeCardStyle}">
        ${m.badge ? `<div style="
          position: absolute; top: -8px; right: -8px;
          background: linear-gradient(135deg, #ffd700, #ffed4e); color: #333;
          padding: 3px 8px; border-radius: 12px; font-size: 9px; font-weight: bold;
          box-shadow: 0 2px 8px rgba(255,215,0,0.5);
        ">${m.badge}</div>` : ''}
        <div style="font-size: 36px; margin-bottom: 6px;" aria-hidden="true">${m.icon}</div>
        <h3 style="color: white; margin: 4px 0; font-size: 14px;">${m.name}</h3>
        <p style="color: rgba(255,255,255,0.7); font-size: 11px;">${m.desc}</p>
      </div>
    `).join('');
        menu.innerHTML = `
      <!-- Profile (top right) -->
      <div style="
        position: fixed; top: 16px; right: 16px;
        display: flex; align-items: center; gap: 10px;
        background: rgba(255,255,255,0.1); padding: 8px 16px;
        border-radius: 50px; cursor: pointer; transition: all 0.3s; z-index: 1001;
      " onclick="game.openProfile()" onmouseover="this.style.background='rgba(255,255,255,0.2)'" onmouseout="this.style.background='rgba(255,255,255,0.1)'">
        <div style="
          width: 36px; height: 36px; background: rgba(255,255,255,0.2);
          border-radius: 50%; display: flex; align-items: center;
          justify-content: center; font-size: 22px; border: 2px solid white;
        ">${avatarIcon}</div>
        <div style="color: white;">
          <div style="font-weight: 600; font-size: 14px;">${username}</div>
          <div style="opacity: 0.7; font-size: 12px;">Level ${level}</div>
        </div>
      </div>

      <!-- Title -->
      <div style="margin-top: 60px; text-align: center; margin-bottom: 24px;">
        <h1 style="font-size: clamp(36px, 8vw, 64px); color: white; margin-bottom: 8px; text-shadow: 0 0 30px rgba(255,255,255,0.3);">
          DOMINAUTS
        </h1>
        <p style="color: rgba(255,255,255,0.8); font-size: clamp(14px, 3vw, 18px);">
          Choose a game mode to get started
        </p>
      </div>

      <!-- Game Mode Grid (responsive) -->
      <div id="modeSelection" style="
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px; max-width: 700px; width: 100%; margin-bottom: 20px;
      ">${modeCards}</div>

      <!-- Action Panel (appears after mode selection) -->
      <div id="actionPanel" style="
        background: rgba(0,0,0,0.3); backdrop-filter: blur(15px);
        border: 1px solid rgba(255,255,255,0.2); border-radius: 20px;
        padding: 24px 32px; max-width: 600px; width: 100%;
        display: none; text-align: center;
        animation: fadeIn 0.3s ease-out;
      ">
        <div id="actionModeTitle" style="color: #ffd700; font-size: 20px; font-weight: 600; margin-bottom: 4px;"></div>
        <div id="actionModeDesc" style="color: rgba(255,255,255,0.7); font-size: 14px; margin-bottom: 16px;"></div>

        <!-- Difficulty Selector -->
        <div style="margin-bottom: 20px;">
          <div style="color: rgba(255,255,255,0.6); font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px;">
            AI Difficulty
          </div>
          <div id="difficultyPicker" style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
            <button class="diff-btn" data-diff="easy" style="
              background: rgba(76,175,80,0.2); border: 2px solid rgba(76,175,80,0.5);
              color: #81c784; padding: 10px 20px; border-radius: 12px; cursor: pointer;
              font-size: 15px; font-weight: 600; transition: all 0.2s; min-width: 100px;
            ">🌱 Easy</button>
            <button class="diff-btn" data-diff="medium" style="
              background: rgba(255,193,7,0.2); border: 2px solid rgba(255,193,7,0.5);
              color: #ffd54f; padding: 10px 20px; border-radius: 12px; cursor: pointer;
              font-size: 15px; font-weight: 600; transition: all 0.2s; min-width: 100px;
            ">🎯 Medium</button>
            <button class="diff-btn" data-diff="hard" style="
              background: rgba(244,67,54,0.2); border: 2px solid rgba(244,67,54,0.5);
              color: #e57373; padding: 10px 20px; border-radius: 12px; cursor: pointer;
              font-size: 15px; font-weight: 600; transition: all 0.2s; min-width: 100px;
            ">🔥 Hard</button>
            <button class="diff-btn" data-diff="expert" style="
              background: rgba(156,39,176,0.2); border: 2px solid rgba(156,39,176,0.5);
              color: #ce93d8; padding: 10px 20px; border-radius: 12px; cursor: pointer;
              font-size: 15px; font-weight: 600; transition: all 0.2s; min-width: 100px;
            ">💀 Expert</button>
          </div>
        </div>

        <!-- Start + How to Play -->
        <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
          <button id="startGameBtn" class="menu-button" style="
            padding: 14px 48px; font-size: 18px; font-weight: 700;
            background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
            box-shadow: 0 4px 20px rgba(76,175,80,0.5);
          ">START GAME</button>
          <button id="howToPlayLink" style="
            background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.3);
            color: rgba(255,255,255,0.9); padding: 14px 24px; border-radius: 30px;
            cursor: pointer; font-size: 15px; transition: all 0.2s;
          ">📖 How to Play</button>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div style="
        margin-top: auto; padding: 16px 0 8px 0;
        display: flex; gap: 16px; align-items: center; flex-wrap: wrap; justify-content: center;
      ">
        <button style="background: none; border: none; color: white; cursor: pointer; font-size: 22px; min-height: 44px; min-width: 44px;"
                onclick="game.toggleSound()" title="Toggle Sound" aria-label="Toggle Sound">🔊</button>
        <button style="background: none; border: none; color: white; cursor: pointer; font-size: 22px; min-height: 44px; min-width: 44px;"
                onclick="game.showAchievements()" title="Achievements" aria-label="Achievements">🏆</button>
        <button style="background: none; border: none; color: white; cursor: pointer; font-size: 22px; min-height: 44px; min-width: 44px;"
                onclick="game.showStats()" title="Statistics" aria-label="Statistics">📊</button>
        <button style="background: none; border: none; color: white; cursor: pointer; font-size: 22px; min-height: 44px; min-width: 44px;"
                onclick="game.showMatchHistory()" title="Match History" aria-label="Match History">📜</button>
        <button style="
          background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.3);
          color: white; cursor: pointer; font-size: 13px; padding: 8px 16px;
          border-radius: 20px; min-height: 44px;
        " onclick="game.showAuthModal()" title="Sign In / Sign Up">
          ${this.authManager.isLoggedIn() && !this.authManager.getUser()?.isGuest
            ? this.authManager.getUser()?.username : 'Sign In'}
        </button>
      </div>
      <div style="display: flex; gap: 20px; padding-bottom: 12px;">
        <a href="/privacy.html" style="color: rgba(255,255,255,0.5); font-size: 12px; text-decoration: none;" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <a href="/terms.html" style="color: rgba(255,255,255,0.5); font-size: 12px; text-decoration: none;" target="_blank" rel="noopener noreferrer">Terms of Service</a>
      </div>
    `;
        document.body.appendChild(menu);
        // --- Wire up interactions ---
        const cards = menu.querySelectorAll('.game-mode-card');
        const actionPanel = document.getElementById('actionPanel');
        const actionTitle = document.getElementById('actionModeTitle');
        const actionDesc = document.getElementById('actionModeDesc');
        let selectedDifficulty = this.difficulty;
        const selectMode = (card) => {
            const mode = card.dataset.mode;
            this.gameMode = mode;
            const rules = getGameRules(mode);
            // Highlight selected card
            cards.forEach(c => {
                c.style.background = 'rgba(255,255,255,0.1)';
                c.style.border = '2px solid rgba(255,255,255,0.3)';
            });
            card.style.background = 'rgba(255,255,255,0.25)';
            card.style.border = '2px solid #ffd700';
            // Show action panel
            actionTitle.textContent = `${rules.icon} ${rules.name}`;
            actionDesc.textContent = rules.description;
            actionPanel.style.display = 'block';
            this.playSound('click');
        };
        cards.forEach(card => {
            card.addEventListener('click', () => selectMode(card));
            card.addEventListener('keydown', (e) => {
                const ke = e;
                if (ke.key === 'Enter' || ke.key === ' ') {
                    ke.preventDefault();
                    selectMode(card);
                }
            });
            card.addEventListener('mouseenter', () => {
                card.style.transform = 'translateY(-3px) scale(1.03)';
                card.style.boxShadow = '0 8px 24px rgba(255,255,255,0.15)';
            });
            card.addEventListener('mouseleave', () => {
                card.style.transform = '';
                card.style.boxShadow = '';
            });
        });
        // Difficulty buttons
        const diffBtns = menu.querySelectorAll('.diff-btn');
        const highlightDiff = (diff) => {
            diffBtns.forEach(b => {
                b.style.opacity = b.dataset.diff === diff ? '1' : '0.5';
                b.style.transform = b.dataset.diff === diff ? 'scale(1.05)' : 'scale(1)';
            });
        };
        highlightDiff(selectedDifficulty);
        diffBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                selectedDifficulty = btn.dataset.diff;
                highlightDiff(selectedDifficulty);
                this.playSound('click');
            });
        });
        // Start Game button
        document.getElementById('startGameBtn').onclick = () => {
            this.startGame(selectedDifficulty);
        };
        // How to Play link
        document.getElementById('howToPlayLink').onclick = () => {
            this.showSelectedModeRules();
        };
        // Pre-select mode from onboarding
        if (preselectedMode) {
            const preCard = menu.querySelector(`[data-mode="${preselectedMode}"]`);
            if (preCard)
                selectMode(preCard);
        }
        // Responsive: 2 cols on mobile
        const modeGrid = document.getElementById('modeSelection');
        const applyResponsive = () => {
            if (window.innerWidth <= 600) {
                modeGrid.style.gridTemplateColumns = 'repeat(2, 1fr)';
            }
            else {
                modeGrid.style.gridTemplateColumns = 'repeat(4, 1fr)';
            }
        };
        applyResponsive();
        window.addEventListener('resize', applyResponsive);
    }
    openProfile() {
        this.profileDashboard.open();
    }
    showSelectedModeRules() {
        const rulesModal = document.createElement('div');
        rulesModal.id = 'rulesModal';
        rulesModal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.85); display: flex; align-items: center;
      justify-content: center; z-index: 4000; padding: 20px;
    `;
        const howToPlayHTML = getHowToPlayText(this.gameMode);
        rulesModal.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
        border: 2px solid rgba(255,215,0,0.4); padding: clamp(20px, 4vw, 40px);
        border-radius: 20px; max-width: 600px; width: 100%;
        max-height: 85vh; overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
        color: #e2e8f0;
      ">
        ${howToPlayHTML}

        <div style="display: flex; gap: 12px; justify-content: center; margin-top: 24px; flex-wrap: wrap;">
          <button onclick="document.getElementById('rulesModal').remove(); game.playSound('click');" style="
            padding: 12px 32px; background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.3); color: white;
            font-size: 16px; border-radius: 30px; cursor: pointer; transition: all 0.2s;
          " onmouseover="this.style.background='rgba(255,255,255,0.2)'" onmouseout="this.style.background='rgba(255,255,255,0.1)'">
            Got it!
          </button>
          <button onclick="document.getElementById('rulesModal').remove(); game.startGame(game.difficulty);" style="
            padding: 12px 32px;
            background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
            border: none; color: white; font-size: 16px; font-weight: 600;
            border-radius: 30px; cursor: pointer; transition: all 0.2s;
            box-shadow: 0 4px 15px rgba(76,175,80,0.4);
          " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
            Start Game
          </button>
        </div>
      </div>
    `;
        document.body.appendChild(rulesModal);
        this.playSound('powerup');
    }
    showModePreview(mode) {
        const preview = document.getElementById('modePreview');
        if (!preview)
            return;
        preview.style.display = 'block';
        const rules = getGameRules(mode);
        const title = document.getElementById('previewTitle');
        const canvas = document.getElementById('previewCanvas');
        const description = document.getElementById('previewDescription');
        const features = document.getElementById('previewFeatures');
        const difficulty = document.getElementById('difficultyStars');
        const playerCount = document.getElementById('playerCount');
        const targetScore = document.getElementById('targetScore');
        const gameStyle = document.getElementById('gameStyle');
        if (title)
            title.innerHTML = `${rules.icon} ${rules.name}`;
        // Create mini canvas preview
        if (canvas) {
            canvas.innerHTML = this.createMiniPreview(mode);
        }
        if (description) {
            description.innerHTML = `
        <strong>${rules.description}</strong><br>
        <span style="opacity: 0.8;">${rules.winCondition}</span>
      `;
        }
        if (features) {
            const topFeatures = rules.specialRules.slice(0, 3);
            features.innerHTML = `
        <div style="margin-bottom: 5px; font-weight: bold;">✨ Key Features:</div>
        ${topFeatures.map(f => `• ${f}`).join('<br>')}
      `;
        }
        // Difficulty rating
        if (difficulty) {
            const diffLevel = this.getModeDifficulty(mode);
            difficulty.innerHTML = '⭐'.repeat(diffLevel) + '☆'.repeat(5 - diffLevel);
        }
        if (playerCount)
            playerCount.textContent = `${rules.playerCount} players`;
        if (targetScore)
            targetScore.textContent = `${rules.maxScore} points`;
        if (gameStyle)
            gameStyle.textContent = this.getGameStyle(mode);
    }
    createMiniPreview(mode) {
        // Create a visual representation of how the table looks for each mode
        const previews = {
            classic: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1e5128 0%, #0d2818 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        ">
          <div style="
            display: flex;
            gap: 5px;
            align-items: center;
          ">
            <div style="width: 40px; height: 20px; background: white; border: 2px solid #333; border-radius: 3px;"></div>
            <div style="width: 40px; height: 20px; background: white; border: 2px solid #333; border-radius: 3px;"></div>
            <div style="width: 40px; height: 20px; background: white; border: 2px solid #333; border-radius: 3px;"></div>
          </div>
          <div style="position: absolute; bottom: 10px; color: white; font-size: 12px;">Green Felt Table</div>
        </div>
      `,
            partner: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1e3a5f 0%, #0a1929 100%);
          border-radius: 10px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="position: absolute; top: 20px; color: #00ff00;">Partner</div>
          <div style="position: absolute; bottom: 20px; color: #00ff00;">You</div>
          <div style="position: absolute; left: 20px; color: #ff6b6b;">Opp 1</div>
          <div style="position: absolute; right: 20px; color: #ff6b6b;">Opp 2</div>
          <div style="color: white;">2v2</div>
        </div>
      `,
            cutthroat: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #5c1e1e 0%, #2d0d0d 100%);
          border-radius: 10px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="position: absolute; top: 20px; color: #ffd700;">Player 2</div>
          <div style="position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); color: #00ff00;">You</div>
          <div style="position: absolute; left: 20px; top: 50%; transform: translateY(-50%); color: #ff6b6b;">Player 3</div>
          <div style="color: white; font-size: 24px;">🎭</div>
        </div>
      `,
            sixlove: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1e3a5f 0%, #0a1929 100%);
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
        ">
          <div style="font-size: 30px;">🇯🇲</div>
          <div style="color: #ffd700; font-weight: bold;">6-LOVE</div>
          <div style="color: white; font-size: 12px;">Win 6 in a row!</div>
        </div>
      `,
            cross: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #3d1e5c 0%, #1a0d2d 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        ">
          <div style="
            width: 80px;
            height: 2px;
            background: white;
            position: absolute;
          "></div>
          <div style="
            width: 2px;
            height: 80px;
            background: white;
            position: absolute;
          "></div>
          <div style="
            width: 30px;
            height: 30px;
            border: 2px solid #ffd700;
            border-radius: 50%;
            background: rgba(255,215,0,0.2);
          "></div>
          <div style="position: absolute; bottom: 10px; color: white; font-size: 12px;">4-Way Layout</div>
        </div>
      `,
            allfives: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #3d1e5c 0%, #1a0d2d 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
        ">
          <div style="
            padding: 10px;
            background: rgba(255,215,0,0.3);
            border: 2px solid #ffd700;
            border-radius: 5px;
          ">
            <div style="color: white; font-size: 20px;">5</div>
          </div>
          <div style="color: white;">+</div>
          <div style="
            padding: 10px;
            background: rgba(255,215,0,0.3);
            border: 2px solid #ffd700;
            border-radius: 5px;
          ">
            <div style="color: white; font-size: 20px;">10</div>
          </div>
          <div style="position: absolute; bottom: 10px; color: #ffd700; font-size: 12px;">Score Zones</div>
        </div>
      `,
            draw: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #3d1e5c 0%, #1a0d2d 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="font-size: 60px; animation: pulse 1s infinite;">⚡</div>
          <div style="position: absolute; bottom: 10px; color: white; font-size: 12px;">Fast-Paced Action!</div>
        </div>
      `,
            block: `
        <div style="
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1e5128 0%, #0d2818 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="font-size: 60px;">🚫</div>
          <div style="position: absolute; bottom: 10px; color: white; font-size: 12px;">Strategic Blocking</div>
        </div>
      `
        };
        return previews[mode] || previews.classic;
    }
    getModeDifficulty(mode) {
        const difficulties = {
            classic: 2,
            draw: 1,
            block: 3,
            allfives: 3,
            cross: 4,
            cutthroat: 4,
            partner: 3,
            sixlove: 5
        };
        return difficulties[mode] || 2;
    }
    getGameStyle(mode) {
        const styles = {
            classic: 'Casual',
            draw: 'Fast',
            block: 'Strategic',
            allfives: 'Points',
            cross: 'Complex',
            cutthroat: 'Competitive',
            partner: 'Team',
            sixlove: 'Tournament'
        };
        return styles[mode] || 'Standard';
    }
    startGame(difficulty) {
        this.difficulty = difficulty;
        // Remove menu and how-to-play button
        const menu = document.getElementById('mainMenu');
        if (menu)
            menu.remove();
        const howToPlayBtn = document.getElementById('howToPlayBtn');
        if (howToPlayBtn)
            howToPlayBtn.remove();
        // Reset multi-player state
        this.teamScores = { team1: 0, team2: 0 };
        this.consecutivePasses = 0;
        // Initialize game
        this.gameStarted = true;
        this.gameStartTime = Date.now();
        this.lastMoveTime = Date.now();
        this.createDominoes();
        this.dealTiles();
        this.setupPowerUps();
        this.addGameControls();
        // If AI goes first, trigger their turn
        if (this.currentPlayerIndex !== 0) {
            this.showMessage(`${this.getPlayerName(this.currentPlayerIndex)} plays first!`, 2000);
            setTimeout(() => this.aiTurnForPlayer(this.currentPlayerIndex), 1500);
        }
        // Show tutorial for new players
        if (this.showTutorial && difficulty === 'easy') {
            this.showGameTutorial();
        }
        this.playSound('click');
    }
    createDominoes() {
        this.tiles = [];
        for (let i = 0; i <= 6; i++) {
            for (let j = i; j <= 6; j++) {
                this.tiles.push({
                    left: i,
                    right: j,
                    x: 0,
                    y: 0,
                    rotation: 0,
                    scale: 1,
                    glowIntensity: 0
                });
            }
        }
        this.shuffleTiles();
    }
    shuffleTiles() {
        for (let i = this.tiles.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.tiles[i], this.tiles[j]] = [this.tiles[j], this.tiles[i]];
        }
    }
    dealTiles() {
        const rules = getGameRules(this.gameMode);
        const tilesPerPlayer = rules.tilesPerPlayer;
        this.playerCount = rules.playerCount;
        // Reset hands
        this.playerHand = [];
        this.aiHand = [];
        this.ai2Hand = [];
        this.ai3Hand = [];
        // Deal tiles to all players
        for (let i = 0; i < tilesPerPlayer; i++) {
            this.playerHand.push(this.tiles.pop());
            this.aiHand.push(this.tiles.pop());
            if (this.playerCount >= 3) {
                this.ai2Hand.push(this.tiles.pop());
            }
            if (this.playerCount >= 4) {
                this.ai3Hand.push(this.tiles.pop());
            }
        }
        // Determine first player based on mode rules
        this.currentPlayerIndex = this.determineFirstPlayer();
        this.currentPlayer = this.currentPlayerIndex === 0 ? 'player' : 'ai';
        this.consecutivePasses = 0;
        this.arrangePlayerHand();
    }
    determineFirstPlayer() {
        const rules = getGameRules(this.gameMode);
        const allHands = [this.playerHand, this.aiHand, this.ai2Hand, this.ai3Hand]
            .slice(0, this.playerCount);
        // Six-Love starts with double-six holder
        if (this.gameMode === 'sixlove' || rules.startingTile === 'double') {
            for (let p = 0; p < allHands.length; p++) {
                for (const tile of allHands[p]) {
                    if (tile.left === 6 && tile.right === 6)
                        return p;
                }
            }
            // Fallback: highest double
            for (let d = 5; d >= 0; d--) {
                for (let p = 0; p < allHands.length; p++) {
                    for (const tile of allHands[p]) {
                        if (tile.left === d && tile.right === d)
                            return p;
                    }
                }
            }
        }
        return 0; // Default: player goes first
    }
    getHandForPlayer(index) {
        switch (index) {
            case 0: return this.playerHand;
            case 1: return this.aiHand;
            case 2: return this.ai2Hand;
            case 3: return this.ai3Hand;
            default: return this.playerHand;
        }
    }
    getPlayerName(index) {
        if (index === 0)
            return 'You';
        return this.aiNames[index - 1] || `AI ${index}`;
    }
    getPlayerTeam(index) {
        // In partner/sixlove: player(0) + AI2(2) = team1, AI1(1) + AI3(3) = team2
        return (index === 0 || index === 2) ? 1 : 2;
    }
    advanceTurn() {
        this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.playerCount;
        this.currentPlayer = this.currentPlayerIndex === 0 ? 'player' : 'ai';
        this.nudgeShownThisTurn = false;
        this.failedAttempts = 0;
    }
    arrangePlayerHand() {
        const w = window.innerWidth;
        const handCount = this.playerHand.length;
        // Responsive spacing: shrink on small screens and large hands
        const maxSpacing = Math.min(80, (w - 60) / Math.max(handCount, 1));
        const spacing = Math.max(40, maxSpacing);
        const totalWidth = (handCount - 1) * spacing;
        const startX = Math.max(30, (w - totalWidth) / 2);
        // Keep hand in safe area above bottom
        const y = window.innerHeight - Math.max(120, window.innerHeight * 0.14);
        this.playerHand.forEach((tile, index) => {
            tile.targetX = startX + index * spacing;
            tile.targetY = y;
            tile.rotation = 0;
            // Smooth animation
            if (!tile.x)
                tile.x = tile.targetX;
            if (!tile.y)
                tile.y = tile.targetY;
        });
    }
    setupPowerUps() {
        const powerUpTypes = ['hint', 'undo', 'peek', 'double-score'];
        const y = window.innerHeight - 250;
        const spacing = 100;
        const startX = (window.innerWidth - (powerUpTypes.length - 1) * spacing) / 2;
        this.powerUps = powerUpTypes.map((type, index) => ({
            type,
            x: startX + index * spacing,
            y,
            active: true,
            cooldown: 0,
            icon: this.getPowerUpIcon(type)
        }));
    }
    addGameControls() {
        // Pause button
        const pauseBtn = document.createElement('button');
        pauseBtn.id = 'pauseBtn';
        pauseBtn.innerHTML = '⏸️';
        pauseBtn.title = 'Pause Game';
        pauseBtn.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 50px;
      height: 50px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid white;
      border-radius: 50%;
      color: white;
      font-size: 24px;
      cursor: pointer;
      z-index: 1000;
      transition: all 0.3s;
    `;
        pauseBtn.onclick = () => this.togglePause();
        document.body.appendChild(pauseBtn);
        // Reset button
        const resetBtn = document.createElement('button');
        resetBtn.id = 'resetBtn';
        resetBtn.innerHTML = '🔄';
        resetBtn.title = 'Reset Game';
        resetBtn.style.cssText = `
      position: fixed;
      top: 20px;
      right: 80px;
      width: 50px;
      height: 50px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid white;
      border-radius: 50%;
      color: white;
      font-size: 24px;
      cursor: pointer;
      z-index: 1000;
      transition: all 0.3s;
    `;
        resetBtn.onclick = () => this.resetGame();
        document.body.appendChild(resetBtn);
        // Hint button - voluntary, player-initiated
        const hintBtn = document.createElement('button');
        hintBtn.id = 'hintBtn';
        hintBtn.innerHTML = '💡';
        hintBtn.title = 'Show Hint';
        hintBtn.style.cssText = `
      position: fixed;
      top: 20px;
      right: 140px;
      width: 50px;
      height: 50px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid white;
      border-radius: 50%;
      color: white;
      font-size: 24px;
      cursor: pointer;
      z-index: 1000;
      transition: all 0.3s;
    `;
        hintBtn.onclick = () => this.voluntaryHint();
        document.body.appendChild(hintBtn);
    }
    /**
     * Player-initiated hint. Only shows when the player taps the hint button.
     */
    voluntaryHint() {
        if (!this.gameStarted || this.currentPlayerIndex !== 0)
            return;
        this.playSound('click');
        const validMoves = [];
        for (const tile of this.playerHand) {
            if (this.canPlayOnSide(tile, 'left'))
                validMoves.push({ tile, side: 'left' });
            if (this.canPlayOnSide(tile, 'right'))
                validMoves.push({ tile, side: 'right' });
        }
        if (validMoves.length === 0) {
            const rules = getGameRules(this.gameMode);
            if (rules.canDraw && this.tiles.length > 0) {
                this.showMessage('No matching tiles - draw from the pile!', 3000);
                this.showDrawButton();
            }
            else {
                this.showMessage('No valid moves - you need to pass', 3000);
                this.showPassButton();
            }
            return;
        }
        // Highlight valid tiles
        this.hints = validMoves;
        this.showHint = true;
        const matchInfo = this.board.length === 0
            ? 'Any tile can start the game'
            : `Match ${this.boardLeftEnd} (left) or ${this.boardRightEnd} (right)`;
        this.showMessage(`${validMoves.length} playable tile${validMoves.length > 1 ? 's' : ''} - ${matchInfo}`, 5000);
        setTimeout(() => { this.showHint = false; this.hints = []; }, 6000);
    }
    togglePause() {
        this.isPaused = !this.isPaused;
        const pauseBtn = document.getElementById('pauseBtn');
        if (pauseBtn) {
            pauseBtn.innerHTML = this.isPaused ? '▶️' : '⏸️';
        }
        if (this.isPaused) {
            // Cancel any pending AI turn to prevent moves during pause
            if (this.pendingAITimeout) {
                clearTimeout(this.pendingAITimeout);
                this.pendingAITimeout = 0;
            }
            this.showPauseMenu();
        }
        else {
            this.closePauseMenu();
            // Resume AI turn if it's not the player's turn
            if (this.currentPlayerIndex !== 0 && this.gameStarted) {
                this.scheduleNextTurn();
            }
        }
    }
    showPauseMenu() {
        this.closePauseMenu();
        const rules = getGameRules(this.gameMode);
        const elapsed = Math.floor(this.timeElapsed);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        const pauseMenu = document.createElement('div');
        pauseMenu.id = 'pauseMenu';
        pauseMenu.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.85); display: flex; align-items: center;
      justify-content: center; z-index: 5000; backdrop-filter: blur(8px);
      padding: 20px;
    `;
        const btnStyle = `
      width: 100%; padding: 14px 20px; border: none; color: white;
      font-size: 16px; font-weight: 600; border-radius: 12px; cursor: pointer;
      transition: all 0.2s; display: flex; align-items: center; gap: 12px;
      justify-content: center;
    `;
        pauseMenu.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
        border: 1px solid rgba(255,255,255,0.15); border-radius: 24px;
        padding: clamp(24px, 4vw, 40px); max-width: 420px; width: 100%;
        box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
        animation: slideInFromTop 0.25s ease-out;
      ">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 20px;">
          <div style="font-size: 40px; margin-bottom: 8px;">⏸</div>
          <h2 style="color: white; font-size: 24px; margin: 0;">Game Paused</h2>
        </div>

        <!-- Game Info -->
        <div style="
          background: rgba(255,255,255,0.06); border-radius: 14px;
          padding: 14px; margin-bottom: 20px;
          display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
        ">
          <div style="text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Mode</div>
            <div style="color: white; font-size: 15px; font-weight: 600; margin-top: 2px;">${rules.icon} ${rules.name}</div>
          </div>
          <div style="text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Difficulty</div>
            <div style="color: white; font-size: 15px; font-weight: 600; margin-top: 2px;">${this.difficulty.charAt(0).toUpperCase() + this.difficulty.slice(1)}</div>
          </div>
          <div style="text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Score</div>
            <div style="color: #ffd700; font-size: 15px; font-weight: 600; margin-top: 2px;">${this.playerScore}</div>
          </div>
          <div style="text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Time</div>
            <div style="color: white; font-size: 15px; font-weight: 600; margin-top: 2px;">${timeStr}</div>
          </div>
          <div style="text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Round</div>
            <div style="color: white; font-size: 15px; font-weight: 600; margin-top: 2px;">${this.roundNumber}</div>
          </div>
          <div style="text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Moves</div>
            <div style="color: white; font-size: 15px; font-weight: 600; margin-top: 2px;">${this.movesCount}</div>
          </div>
        </div>

        <!-- Sound Settings -->
        <div style="
          background: rgba(255,255,255,0.06); border-radius: 14px;
          padding: 14px; margin-bottom: 20px;
        ">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;">
            <span style="color: #e2e8f0; font-size: 14px;">Sound Effects</span>
            <button id="sfxToggle" style="
              background: ${this.soundEnabled ? '#4CAF50' : 'rgba(255,255,255,0.15)'};
              border: none; color: white; padding: 6px 16px; border-radius: 20px;
              cursor: pointer; font-size: 13px; transition: all 0.2s; min-width: 56px;
            ">${this.soundEnabled ? 'ON' : 'OFF'}</button>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <span style="color: #e2e8f0; font-size: 14px;">Music Volume</span>
            <input id="musicSlider" type="range" min="0" max="100"
                   value="${Math.round(this.musicVolume * 100)}"
                   style="width: 120px; accent-color: #667eea; cursor: pointer;" />
          </div>
        </div>

        <!-- Actions -->
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="resumeBtn" style="${btnStyle} background: linear-gradient(135deg, #4CAF50, #2E7D32); font-size: 18px; padding: 16px 20px;">
            Resume Game
          </button>

          <button id="howToPlayPauseBtn" style="${btnStyle} background: rgba(255,255,255,0.1);">
            📖 How to Play
          </button>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
            <button id="restartBtn" style="${btnStyle} background: rgba(255,193,7,0.2); color: #ffd54f;">
              🔄 Restart
            </button>
            <button id="quitBtn" style="${btnStyle} background: rgba(244,67,54,0.2); color: #ef9a9a;">
              🏠 Quit
            </button>
          </div>
        </div>
      </div>
    `;
        document.body.appendChild(pauseMenu);
        // Wire up buttons
        document.getElementById('resumeBtn').onclick = () => {
            this.playSound('click');
            this.togglePause();
        };
        document.getElementById('howToPlayPauseBtn').onclick = () => {
            this.playSound('click');
            this.showSelectedModeRules();
        };
        document.getElementById('sfxToggle').onclick = () => {
            this.soundEnabled = !this.soundEnabled;
            this.playSound('click');
            const btn = document.getElementById('sfxToggle');
            btn.textContent = this.soundEnabled ? 'ON' : 'OFF';
            btn.style.background = this.soundEnabled ? '#4CAF50' : 'rgba(255,255,255,0.15)';
        };
        document.getElementById('musicSlider').oninput = (e) => {
            this.musicVolume = parseInt(e.target.value) / 100;
        };
        document.getElementById('restartBtn').onclick = () => {
            this.playSound('click');
            this.showPauseConfirm('Restart this game?', 'All progress in this round will be lost.', () => {
                this.restartGame();
            });
        };
        document.getElementById('quitBtn').onclick = () => {
            this.playSound('click');
            this.showPauseConfirm('Quit to main menu?', 'Your current game will not be saved.', () => {
                location.reload();
            });
        };
        // Hover effects on all buttons
        pauseMenu.querySelectorAll('button').forEach(btn => {
            btn.addEventListener('mouseenter', () => { btn.style.transform = 'scale(1.02)'; });
            btn.addEventListener('mouseleave', () => { btn.style.transform = 'scale(1)'; });
        });
        // Close on ESC key
        const escHandler = (e) => {
            if (e.key === 'Escape' && this.isPaused) {
                this.togglePause();
                document.removeEventListener('keydown', escHandler);
            }
        };
        document.addEventListener('keydown', escHandler);
    }
    showPauseConfirm(title, message, onConfirm) {
        const dialog = document.createElement('div');
        dialog.id = 'pauseConfirm';
        dialog.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.6); display: flex; align-items: center;
      justify-content: center; z-index: 6000; padding: 20px;
    `;
        dialog.innerHTML = `
      <div style="
        background: #1e293b; border: 1px solid rgba(255,255,255,0.2);
        border-radius: 20px; padding: 28px; max-width: 360px; width: 100%;
        text-align: center; box-shadow: 0 16px 48px rgba(0,0,0,0.5);
      ">
        <h3 style="color: white; font-size: 20px; margin-bottom: 8px;">${title}</h3>
        <p style="color: #94a3b8; font-size: 14px; margin-bottom: 24px;">${message}</p>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button id="confirmCancel" style="
            padding: 10px 28px; background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.2); color: white;
            border-radius: 12px; cursor: pointer; font-size: 15px;
          ">Cancel</button>
          <button id="confirmOk" style="
            padding: 10px 28px; background: #ef4444;
            border: none; color: white; border-radius: 12px;
            cursor: pointer; font-size: 15px; font-weight: 600;
          ">Confirm</button>
        </div>
      </div>
    `;
        document.body.appendChild(dialog);
        document.getElementById('confirmCancel').onclick = () => { dialog.remove(); this.playSound('click'); };
        document.getElementById('confirmOk').onclick = () => { dialog.remove(); onConfirm(); };
    }
    closePauseMenu() {
        const pauseMenu = document.getElementById('pauseMenu');
        if (pauseMenu) {
            pauseMenu.remove();
        }
    }
    restartGame() {
        // Reset all game state
        this.tiles = [];
        this.playerHand = [];
        this.aiHand = [];
        this.ai2Hand = [];
        this.ai3Hand = [];
        this.board = [];
        this.boardLeftEnd = -1;
        this.boardRightEnd = -1;
        this.currentPlayer = 'player';
        this.currentPlayerIndex = 0;
        this.playerScore = 0;
        this.aiScore = 0;
        this.ai2Score = 0;
        this.combo = 0;
        this.movesCount = 0;
        this.timeElapsed = 0;
        this.gameStartTime = Date.now();
        this.isPaused = false;
        this.particles = [];
        this.consecutivePasses = 0;
        // Close pause menu
        this.closePauseMenu();
        // Reinitialize the game
        this.createDominoes();
        this.dealTiles();
        this.gameStarted = true;
        // Update pause button
        const pauseBtn = document.getElementById('pauseBtn');
        if (pauseBtn) {
            pauseBtn.innerHTML = '⏸️';
        }
    }
    /**
     * Quick rematch - same mode and difficulty, no menu.
     */
    rematch() {
        // Remove any end-game screens
        document.querySelectorAll('[id$="Screen"], [id$="screen"]').forEach(el => el.remove());
        document.getElementById('rulesModal')?.remove();
        this.restartGame();
        this.playSound('click');
        this.triggerHaptic('medium');
    }
    resetGame() {
        if (confirm('Are you sure you want to reset the game?')) {
            location.reload();
        }
    }
    showGameTutorial() {
        const tutorial = document.createElement('div');
        tutorial.id = 'gameTutorial';
        tutorial.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
    `;
        // Get mode-specific rules and content
        const rules = getGameRules(this.gameMode);
        const howToPlayHTML = getHowToPlayText(this.gameMode);
        tutorial.innerHTML = `
      <div style="background: rgba(255,255,255,0.1); padding: 40px; border-radius: 20px; max-width: 700px; max-height: 80vh; overflow-y: auto;">
        ${howToPlayHTML}

        <h3 style="color: #00ff00; margin-top: 30px;">✨ Power-Ups (Bottom of screen):</h3>
        <div style="margin-left: 20px; color: white;">
          <p>💡 <b>Hint:</b> Shows you a valid move (glowing green)</p>
          <p>↩️ <b>Undo:</b> Take back your last move</p>
          <p>👁️ <b>Peek:</b> See how many tiles AI has</p>
          ${this.gameMode === 'allfives' ? '<p>2️⃣ <b>Double Score:</b> Double points for next 3 moves</p>' : ''}
        </div>

        <h3 style="color: #00ff00; margin-top: 20px;">💡 Tips:</h3>
        <ul style="margin-left: 20px; color: white;">
          <li>Green glow = valid move</li>
          <li>Red = can't play there</li>
          <li>Watch the tile counts to track AI's progress</li>
          <li>Use power-ups wisely - they have cooldowns!</li>
          ${this.gameMode === 'partner' || this.gameMode === 'sixlove' ? '<li>Team play: Your partner sits opposite you</li>' : ''}
          ${this.gameMode === 'cross' ? '<li>Play in 4 directions from the spinner!</li>' : ''}
        </ul>

        <button onclick="document.getElementById('gameTutorial').remove()" style="
          margin-top: 30px;
          padding: 15px 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
          font-size: 18px;
          border-radius: 30px;
          cursor: pointer;
          width: 100%;
        ">Got it! Let's Play! 🎲</button>
      </div>
    `;
        document.body.appendChild(tutorial);
        this.showTutorial = false;
    }
    getPowerUpIcon(type) {
        switch (type) {
            case 'hint': return '💡';
            case 'undo': return '↩️';
            case 'peek': return '👁️';
            case 'double-score': return '2️⃣';
        }
    }
    animate() {
        const currentTime = Date.now();
        this.deltaTime = currentTime - this.lastFrameTime;
        this.lastFrameTime = currentTime;
        this.animationFrame++;
        if (this.gameStarted) {
            this.timeElapsed = (currentTime - this.gameStartTime) / 1000;
        }
        this.update();
        this.render();
        requestAnimationFrame(() => this.animate());
    }
    update() {
        if (this.isPaused)
            return;
        // Gentle one-time nudge after 25s of inactivity (no auto-hints)
        if (this.gameStarted && this.currentPlayerIndex === 0 && !this.showHint && !this.nudgeShownThisTurn) {
            const inactiveTime = Date.now() - this.lastMoveTime;
            if (inactiveTime > 25000) {
                this.showMessage('Tip: Double-tap a tile to play it, or tap ? for help', 3000);
                this.nudgeShownThisTurn = true;
            }
        }
        // Update animations
        this.pulseAnimation = Math.sin(this.animationFrame * 0.05) * 0.5 + 0.5;
        this.backgroundHue = (this.backgroundHue + 0.1) % 360;
        // Update tiles
        this.playerHand.forEach(tile => {
            if (!tile.isDragging) {
                tile.x += (tile.targetX - tile.x) * 0.1;
                tile.y += (tile.targetY - tile.y) * 0.1;
            }
            if (tile.isHovered) {
                tile.scale = Math.min(tile.scale + 0.02, 1.1);
                tile.glowIntensity = Math.min(tile.glowIntensity + 0.05, 1);
            }
            else {
                tile.scale = Math.max(tile.scale - 0.02, 1);
                tile.glowIntensity = Math.max(tile.glowIntensity - 0.05, 0);
            }
        });
        // Update particles
        this.particles = this.particles.filter(p => {
            p.life -= this.deltaTime / 1000;
            p.y += p.vy;
            p.x += p.vx;
            p.vy += 0.1; // gravity
            return p.life > 0;
        });
        // Update screen shake
        if (this.screenShake > 0) {
            this.screenShake *= 0.9;
        }
        // Update drop zone glow
        if (this.validDropZone) {
            this.dropZoneGlow = Math.min(this.dropZoneGlow + 0.1, 1);
        }
        else {
            this.dropZoneGlow = Math.max(this.dropZoneGlow - 0.1, 0);
        }
        // Update power-up cooldowns
        this.powerUps.forEach(powerUp => {
            if (powerUp.cooldown > 0) {
                powerUp.cooldown -= this.deltaTime / 1000;
                if (powerUp.cooldown <= 0) {
                    powerUp.active = true;
                    powerUp.cooldown = 0;
                }
            }
        });
    }
    render() {
        const ctx = this.ctx;
        // Apply screen shake
        ctx.save();
        if (this.screenShake > 0) {
            const shakeX = (Math.random() - 0.5) * this.screenShake * 10;
            const shakeY = (Math.random() - 0.5) * this.screenShake * 10;
            ctx.translate(shakeX, shakeY);
        }
        // Draw animated background
        this.drawBackground();
        // Draw game board
        if (this.gameStarted) {
            this.drawGameBoard();
            this.drawPlayerHand();
            this.drawPowerUps();
            this.drawScore();
            this.drawCombo();
        }
        // Draw particles
        this.drawParticles();
        // Draw hints
        if (this.showHint && this.hints.length > 0) {
            this.drawHints();
        }
        ctx.restore();
    }
    drawTableSurface() {
        const ctx = this.ctx;
        const width = window.innerWidth;
        const height = window.innerHeight;
        ctx.save();
        // Table surface gradient with variant-specific colors
        const tableGradient = ctx.createRadialGradient(width / 2, height / 2, 50, width / 2, height / 2, Math.max(width, height) * 0.7);
        const colors = this.getTableColors();
        tableGradient.addColorStop(0, colors.center);
        tableGradient.addColorStop(0.6, colors.mid);
        tableGradient.addColorStop(1, colors.edge);
        // Draw table with rounded corners for a more natural look
        const margin = Math.min(width, height) * 0.06;
        const tableTop = height * 0.12;
        const tableBottom = height * 0.88;
        const tableLeft = margin;
        const tableRight = width - margin;
        const radius = 30;
        ctx.fillStyle = tableGradient;
        this.drawRoundedRect(ctx, tableLeft, tableTop, tableRight - tableLeft, tableBottom - tableTop, radius);
        ctx.fill();
        // Wood border with shadow
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 15;
        ctx.strokeStyle = '#5a3015';
        ctx.lineWidth = 10;
        this.drawRoundedRect(ctx, tableLeft, tableTop, tableRight - tableLeft, tableBottom - tableTop, radius);
        ctx.stroke();
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        // Inner wood edge
        ctx.strokeStyle = '#7a4a2a';
        ctx.lineWidth = 3;
        this.drawRoundedRect(ctx, tableLeft + 6, tableTop + 6, tableRight - tableLeft - 12, tableBottom - tableTop - 12, radius - 4);
        ctx.stroke();
        // Felt texture - subtle noise pattern
        ctx.globalAlpha = 0.04;
        ctx.fillStyle = '#ffffff';
        for (let i = 0; i < 80; i++) {
            const tx = tableLeft + Math.random() * (tableRight - tableLeft);
            const ty = tableTop + Math.random() * (tableBottom - tableTop);
            ctx.beginPath();
            ctx.arc(tx, ty, Math.random() * 1.5 + 0.5, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
        // Light pool in center
        const lightGrad = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, 250);
        lightGrad.addColorStop(0, 'rgba(255, 255, 255, 0.06)');
        lightGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = lightGrad;
        ctx.fillRect(tableLeft, tableTop, tableRight - tableLeft, tableBottom - tableTop);
        // Draw mode-specific layout guides
        this.drawModeSpecificLayout(ctx);
        // Add player positions for multiplayer modes
        if (this.playerCount > 2) {
            this.drawPlayerPositions(ctx);
        }
        ctx.restore();
    }
    getTableColors() {
        switch (this.gameMode) {
            case 'classic':
            case 'draw':
                return { center: '#1e5128', mid: '#194520', edge: '#0f2d14' };
            case 'block':
                return { center: '#1a4a24', mid: '#143d1c', edge: '#0c2810' };
            case 'allfives':
                return { center: '#2a1a4a', mid: '#221440', edge: '#150d2a' };
            case 'cross':
                return { center: '#3a1a5a', mid: '#2e1448', edge: '#1a0d30' };
            case 'partner':
                return { center: '#1a3555', mid: '#142c48', edge: '#0c1c30' };
            case 'sixlove':
                return { center: '#1a4050', mid: '#143540', edge: '#0c2028' };
            case 'cutthroat':
                return { center: '#501a1a', mid: '#401414', edge: '#2a0d0d' };
            default:
                return { center: '#1e5128', mid: '#194520', edge: '#0f2d14' };
        }
    }
    drawModeSpecificLayout(ctx) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const centerX = width / 2;
        const centerY = height / 2;
        ctx.save();
        switch (this.gameMode) {
            case 'cross':
                // Draw cross layout guides with subtle glow
                ctx.strokeStyle = 'rgba(200, 180, 255, 0.2)';
                ctx.lineWidth = 2;
                ctx.setLineDash([8, 6]);
                ctx.beginPath();
                ctx.moveTo(centerX, centerY - 180);
                ctx.lineTo(centerX, centerY + 180);
                ctx.moveTo(centerX - 250, centerY);
                ctx.lineTo(centerX + 250, centerY);
                ctx.stroke();
                ctx.setLineDash([]);
                // Glowing center circle for spinner
                const spinnerGlow = ctx.createRadialGradient(centerX, centerY, 15, centerX, centerY, 40);
                spinnerGlow.addColorStop(0, 'rgba(200, 170, 255, 0.15)');
                spinnerGlow.addColorStop(1, 'rgba(200, 170, 255, 0)');
                ctx.fillStyle = spinnerGlow;
                ctx.fillRect(centerX - 40, centerY - 40, 80, 80);
                ctx.strokeStyle = 'rgba(200, 170, 255, 0.3)';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(centerX, centerY, 25, 0, Math.PI * 2);
                ctx.stroke();
                break;
            case 'allfives':
                // Scoring end indicators
                if (this.board.length > 0) {
                    const endTotal = this.boardLeftEnd + this.boardRightEnd;
                    const isScoring = endTotal % 5 === 0 && endTotal > 0;
                    // Left end indicator
                    this.drawEndIndicator(ctx, 120, centerY - 50, this.boardLeftEnd, isScoring);
                    // Right end indicator
                    this.drawEndIndicator(ctx, width - 180, centerY - 50, this.boardRightEnd, isScoring);
                    // Scoring pulse effect
                    if (isScoring) {
                        const pulseAlpha = 0.1 + Math.sin(this.animationFrame * 0.08) * 0.08;
                        const pulseGrad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 200);
                        pulseGrad.addColorStop(0, `rgba(255, 215, 0, ${pulseAlpha})`);
                        pulseGrad.addColorStop(1, 'rgba(255, 215, 0, 0)');
                        ctx.fillStyle = pulseGrad;
                        ctx.fillRect(0, 0, width, height);
                    }
                }
                break;
            case 'partner':
                // Team indicators
                ctx.fillStyle = 'rgba(0, 200, 100, 0.08)';
                this.drawRoundedRect(ctx, width * 0.3, height * 0.14, width * 0.4, 30, 8);
                ctx.fill();
                this.drawRoundedRect(ctx, width * 0.3, height * 0.82, width * 0.4, 30, 8);
                ctx.fill();
                ctx.fillStyle = 'rgba(255, 100, 100, 0.08)';
                this.drawRoundedRect(ctx, width * 0.06, height * 0.42, 30, height * 0.16, 8);
                ctx.fill();
                this.drawRoundedRect(ctx, width * 0.88, height * 0.42, 30, height * 0.16, 8);
                ctx.fill();
                break;
            case 'sixlove':
                // Streak banner at top
                this.drawStreakBanner(ctx);
                // Team indicators same as partner
                ctx.fillStyle = 'rgba(0, 200, 100, 0.08)';
                this.drawRoundedRect(ctx, width * 0.3, height * 0.14, width * 0.4, 30, 8);
                ctx.fill();
                break;
            case 'block':
                // Tense styling - darker overlay near edges
                const edgeGrad = ctx.createRadialGradient(centerX, centerY, 100, centerX, centerY, Math.max(width, height) * 0.5);
                edgeGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
                edgeGrad.addColorStop(1, 'rgba(0, 0, 0, 0.15)');
                ctx.fillStyle = edgeGrad;
                ctx.fillRect(0, 0, width, height);
                break;
            case 'cutthroat':
                // Triangular play area hint
                ctx.strokeStyle = 'rgba(255, 100, 100, 0.1)';
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 8]);
                ctx.beginPath();
                ctx.moveTo(centerX, height * 0.2);
                ctx.lineTo(width * 0.15, height * 0.75);
                ctx.lineTo(width * 0.85, height * 0.75);
                ctx.closePath();
                ctx.stroke();
                ctx.setLineDash([]);
                break;
        }
        ctx.restore();
    }
    drawEndIndicator(ctx, x, y, value, isScoring) {
        ctx.save();
        const color = isScoring ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)';
        ctx.fillStyle = color;
        this.drawRoundedRect(ctx, x, y, 60, 50, 8);
        ctx.fill();
        ctx.fillStyle = isScoring ? '#ffd700' : 'rgba(255, 255, 255, 0.5)';
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${value}`, x + 30, y + 25);
        ctx.restore();
    }
    drawStreakBanner(ctx) {
        const width = window.innerWidth;
        const bannerY = 12;
        const bannerW = 280;
        const bannerH = 40;
        const bannerX = (width - bannerW) / 2;
        ctx.save();
        // Banner background
        const bannerGrad = ctx.createLinearGradient(bannerX, bannerY, bannerX + bannerW, bannerY);
        bannerGrad.addColorStop(0, 'rgba(0, 150, 50, 0.8)');
        bannerGrad.addColorStop(0.5, 'rgba(255, 215, 0, 0.9)');
        bannerGrad.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
        ctx.fillStyle = bannerGrad;
        this.drawRoundedRect(ctx, bannerX, bannerY, bannerW, bannerH, 8);
        ctx.fill();
        // Streak text
        ctx.fillStyle = 'white';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        // Show streak dots
        const t1 = this.sixLoveStreak.team1;
        const t2 = this.sixLoveStreak.team2;
        const dots1 = '●'.repeat(t1) + '○'.repeat(6 - t1);
        const dots2 = '●'.repeat(t2) + '○'.repeat(6 - t2);
        ctx.fillText(`YOU ${dots1}  vs  ${dots2} OPP`, bannerX + bannerW / 2, bannerY + bannerH / 2);
        ctx.restore();
    }
    drawPlayerPositions(ctx) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const rules = getGameRules(this.gameMode);
        const isTeamMode = rules.teamPlay;
        let positions;
        if (this.gameMode === 'cutthroat') {
            positions = [
                { x: width / 2, y: height * 0.87, label: 'You', color: '#00ff00', tiles: this.playerHand.length, isActive: this.currentPlayerIndex === 0 },
                { x: width * 0.2, y: height * 0.35, label: this.aiNames[0], color: '#ff6b6b', tiles: this.aiHand.length, isActive: this.currentPlayerIndex === 1 },
                { x: width * 0.8, y: height * 0.35, label: this.aiNames[1], color: '#ffd700', tiles: this.ai2Hand.length, isActive: this.currentPlayerIndex === 2 },
            ];
        }
        else if (isTeamMode) {
            positions = [
                { x: width / 2, y: height * 0.87, label: 'You', color: '#00ff00', tiles: this.playerHand.length, isActive: this.currentPlayerIndex === 0 },
                { x: width * 0.15, y: height * 0.5, label: this.aiNames[0], color: '#ff6b6b', tiles: this.aiHand.length, isActive: this.currentPlayerIndex === 1 },
                { x: width / 2, y: height * 0.25, label: `${this.aiNames[1]} (Partner)`, color: '#00ccff', tiles: this.ai2Hand.length, isActive: this.currentPlayerIndex === 2 },
                { x: width * 0.85, y: height * 0.5, label: this.aiNames[2], color: '#ff6b6b', tiles: this.ai3Hand.length, isActive: this.currentPlayerIndex === 3 },
            ];
        }
        else {
            // 2-player: show opponent tile count at top center
            positions = [
                { x: width / 2, y: 50, label: this.aiNames[0], color: '#ff6b6b', tiles: this.aiHand.length, isActive: this.currentPlayerIndex === 1 },
            ];
        }
        ctx.save();
        positions.forEach((pos) => {
            // Active player indicator
            if (pos.isActive) {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 55, 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = '#ffd700';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
            // Player circle
            ctx.fillStyle = pos.color;
            ctx.globalAlpha = 0.4;
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, 35, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
            // Tile count inside circle
            ctx.fillStyle = 'white';
            ctx.font = 'bold 20px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${pos.tiles}`, pos.x, pos.y);
            // Label below
            ctx.font = 'bold 13px Arial';
            ctx.fillStyle = pos.color;
            ctx.fillText(pos.label, pos.x, pos.y + 50);
        });
        ctx.restore();
    }
    drawBackground() {
        const ctx = this.ctx;
        const width = window.innerWidth;
        const height = window.innerHeight;
        // Subtle, slow gradient that doesn't distract from gameplay
        const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height));
        // Dark, elegant background that lets the table be the focus
        gradient.addColorStop(0, '#1a1a2e');
        gradient.addColorStop(0.5, '#0f0f1a');
        gradient.addColorStop(1, '#080812');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
    }
    drawGameBoard() {
        const ctx = this.ctx;
        const centerY = window.innerHeight / 2 - 30;
        const centerX = window.innerWidth / 2;
        const width = window.innerWidth;
        // Draw table surface
        this.drawTableSurface();
        // Draw board tiles with auto-scaling
        if (this.board.length > 0) {
            const maxWidth = width * 0.75;
            const baseSpacing = 68;
            const tileSpacing = Math.min(baseSpacing, maxWidth / this.board.length);
            const startX = centerX - ((this.board.length - 1) * tileSpacing) / 2;
            const tileScale = Math.min(0.8, Math.max(0.5, maxWidth / (this.board.length * baseSpacing)));
            this.board.forEach((tile, index) => {
                const x = startX + index * tileSpacing;
                const y = centerY;
                this.drawDomino(tile, x, y, tileScale);
            });
        }
        else {
            // Subtle placeholder
            ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.font = '20px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('Place your first domino', centerX, centerY);
        }
        // Draw drop zones with better visual feedback
        if (this.draggedTile && this.dropZoneGlow > 0) {
            ctx.save();
            const glowAlpha = this.dropZoneGlow * 0.4;
            if (this.validDropZone === 'left') {
                const dropGrad = ctx.createRadialGradient(100, centerY, 0, 100, centerY, 80);
                dropGrad.addColorStop(0, `rgba(0, 255, 120, ${glowAlpha})`);
                dropGrad.addColorStop(1, 'rgba(0, 255, 120, 0)');
                ctx.fillStyle = dropGrad;
                ctx.fillRect(30, centerY - 80, 150, 160);
            }
            else if (this.validDropZone === 'right') {
                const dropGrad = ctx.createRadialGradient(width - 100, centerY, 0, width - 100, centerY, 80);
                dropGrad.addColorStop(0, `rgba(0, 255, 120, ${glowAlpha})`);
                dropGrad.addColorStop(1, 'rgba(0, 255, 120, 0)');
                ctx.fillStyle = dropGrad;
                ctx.fillRect(width - 180, centerY - 80, 150, 160);
            }
            ctx.restore();
        }
    }
    drawPlayerHand() {
        this.playerHand.forEach(tile => {
            if (!tile.isDragging) {
                this.drawDomino(tile, tile.x, tile.y, tile.scale);
            }
        });
        // Draw dragged tile on top
        if (this.draggedTile) {
            this.drawDomino(this.draggedTile, this.draggedTile.x, this.draggedTile.y, 1.2);
        }
    }
    drawDomino(tile, x, y, scale = 1) {
        const ctx = this.ctx;
        const width = 60 * scale;
        const height = 120 * scale;
        const cornerRadius = 6 * scale;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(tile.rotation);
        // Drop shadow for depth
        ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
        ctx.shadowBlur = 8 * scale;
        ctx.shadowOffsetX = 3 * scale;
        ctx.shadowOffsetY = 3 * scale;
        // Draw domino body with rounded corners
        const gradient = ctx.createLinearGradient(0, -height / 2, 0, height / 2);
        gradient.addColorStop(0, '#fafafa');
        gradient.addColorStop(0.3, '#f0f0f0');
        gradient.addColorStop(0.7, '#e8e8e8');
        gradient.addColorStop(1, '#dcdcdc');
        ctx.fillStyle = gradient;
        this.drawRoundedRect(ctx, -width / 2, -height / 2, width, height, cornerRadius);
        ctx.fill();
        // Reset shadow for subsequent draws
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        // Add glow effect for hovered/hint tiles
        if (tile.glowIntensity > 0) {
            ctx.strokeStyle = `rgba(0, 255, 200, ${tile.glowIntensity * 0.8})`;
            ctx.lineWidth = 3 * scale;
            ctx.shadowColor = `rgba(0, 255, 200, ${tile.glowIntensity})`;
            ctx.shadowBlur = 15 * tile.glowIntensity;
            this.drawRoundedRect(ctx, -width / 2, -height / 2, width, height, cornerRadius);
            ctx.stroke();
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
        }
        // Draw border
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 1.5 * scale;
        this.drawRoundedRect(ctx, -width / 2, -height / 2, width, height, cornerRadius);
        ctx.stroke();
        // Draw divider line with subtle inset
        ctx.beginPath();
        ctx.moveTo(-width / 2 + 4 * scale, 0);
        ctx.lineTo(width / 2 - 4 * scale, 0);
        ctx.strokeStyle = '#aaa';
        ctx.lineWidth = 1.5 * scale;
        ctx.stroke();
        // Draw pips with better styling
        this.drawPips(ctx, 0, -height / 4, tile.left, width * 0.55);
        this.drawPips(ctx, 0, height / 4, tile.right, width * 0.55);
        ctx.restore();
    }
    drawPips(ctx, x, y, value, size) {
        const dotSize = size / 7;
        const positions = this.getPipPositions(value);
        positions.forEach(([px, py]) => {
            const pipX = x + px * size / 3;
            const pipY = y + py * size / 3;
            // Pip shadow
            ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
            ctx.beginPath();
            ctx.arc(pipX + 0.5, pipY + 0.5, dotSize, 0, Math.PI * 2);
            ctx.fill();
            // Pip body with gradient
            const pipGrad = ctx.createRadialGradient(pipX - dotSize * 0.3, pipY - dotSize * 0.3, 0, pipX, pipY, dotSize);
            pipGrad.addColorStop(0, '#555');
            pipGrad.addColorStop(1, '#1a1a1a');
            ctx.fillStyle = pipGrad;
            ctx.beginPath();
            ctx.arc(pipX, pipY, dotSize, 0, Math.PI * 2);
            ctx.fill();
        });
    }
    getPipPositions(value) {
        switch (value) {
            case 0: return [];
            case 1: return [[0, 0]];
            case 2: return [[-0.5, -0.5], [0.5, 0.5]];
            case 3: return [[-0.5, -0.5], [0, 0], [0.5, 0.5]];
            case 4: return [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]];
            case 5: return [[-0.5, -0.5], [0.5, -0.5], [0, 0], [-0.5, 0.5], [0.5, 0.5]];
            case 6: return [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0], [0.5, 0], [-0.5, 0.5], [0.5, 0.5]];
            default: return [];
        }
    }
    drawPowerUps() {
        const ctx = this.ctx;
        this.powerUps.forEach(powerUp => {
            ctx.save();
            const scale = powerUp.active ? 1 + Math.sin(this.animationFrame * 0.1) * 0.1 : 0.7;
            ctx.translate(powerUp.x, powerUp.y);
            ctx.scale(scale, scale);
            // Draw power-up background
            ctx.globalAlpha = powerUp.active ? 1 : 0.3;
            ctx.fillStyle = powerUp.active ? 'rgba(255, 215, 0, 0.2)' : 'rgba(100, 100, 100, 0.2)';
            ctx.beginPath();
            ctx.arc(0, 0, 35, 0, Math.PI * 2);
            ctx.fill();
            // Draw border
            ctx.strokeStyle = powerUp.active ? 'rgba(255, 215, 0, 0.5)' : 'rgba(100, 100, 100, 0.5)';
            ctx.lineWidth = 2;
            ctx.stroke();
            // Draw icon
            ctx.font = '28px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(powerUp.icon, 0, -5);
            // Draw label
            ctx.fillStyle = 'white';
            ctx.font = 'bold 11px Arial';
            const label = this.getPowerUpLabel(powerUp.type);
            ctx.fillText(label, 0, 25);
            // Draw cooldown
            if (powerUp.cooldown > 0) {
                ctx.fillStyle = 'yellow';
                ctx.font = '14px Arial';
                ctx.fillText(Math.ceil(powerUp.cooldown) + 's', 0, 45);
            }
            ctx.restore();
        });
    }
    getPowerUpLabel(type) {
        switch (type) {
            case 'hint': return 'HINT';
            case 'undo': return 'UNDO';
            case 'peek': return 'PEEK';
            case 'double-score': return '2X SCORE';
        }
    }
    scaledFont(weight, size, family = 'Arial') {
        const s = Math.min(1, window.innerWidth / 420); // Scale down below 420px
        return `${weight} ${Math.round(size * s)}px ${family}`;
    }
    drawScore() {
        const ctx = this.ctx;
        const rules = getGameRules(this.gameMode);
        const isTeamMode = rules.teamPlay;
        const panelHeight = this.playerCount > 2 ? 280 : 230;
        // Draw score panel
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        const panelWidth = 300;
        this.drawRoundedRect(ctx, 15, 15, panelWidth, panelHeight, 12);
        ctx.fill();
        // Mode badge
        ctx.fillStyle = '#ffd700';
        ctx.font = this.scaledFont('bold', 14);
        ctx.fillText(`${rules.icon} ${rules.name}`, 25, 38);
        // Turn indicator
        ctx.fillStyle = '#00ff00';
        ctx.font = this.scaledFont('', 12);
        const turnName = this.getPlayerName(this.currentPlayerIndex);
        ctx.fillText(`Turn: ${turnName}`, 200, 38);
        let yPos = 58;
        if (isTeamMode) {
            // Team scores
            ctx.fillStyle = '#00ff00';
            ctx.font = 'bold 18px Arial';
            ctx.fillText(`Your Team: ${this.playerScore}`, 25, yPos);
            yPos += 25;
            ctx.fillStyle = '#ff6b6b';
            ctx.fillText(`Opponents: ${this.aiScore}`, 25, yPos);
            yPos += 25;
            // Tile counts for all players
            ctx.font = '13px Arial';
            ctx.fillStyle = '#00ff00';
            ctx.fillText(`You: ${this.playerHand.length}`, 25, yPos);
            ctx.fillStyle = '#00ccff';
            ctx.fillText(`${this.aiNames[1]}: ${this.ai2Hand.length}`, 145, yPos);
            yPos += 18;
            ctx.fillStyle = '#ff6b6b';
            ctx.fillText(`${this.aiNames[0]}: ${this.aiHand.length}`, 25, yPos);
            ctx.fillText(`${this.aiNames[2]}: ${this.ai3Hand.length}`, 145, yPos);
            yPos += 22;
            // Six-Love streak
            if (this.gameMode === 'sixlove') {
                ctx.fillStyle = '#ffd700';
                ctx.font = 'bold 14px Arial';
                ctx.fillText(`Streak: ${this.sixLoveStreak.team1}-${this.sixLoveStreak.team2}`, 25, yPos);
                yPos += 20;
            }
        }
        else if (this.playerCount === 3) {
            // 3-player individual scores
            ctx.font = 'bold 16px Arial';
            ctx.fillStyle = '#00ff00';
            ctx.fillText(`You: ${this.playerScore} (${this.playerHand.length} tiles)`, 25, yPos);
            yPos += 22;
            ctx.fillStyle = '#ff6b6b';
            ctx.fillText(`${this.aiNames[0]}: ${this.aiScore} (${this.aiHand.length} tiles)`, 25, yPos);
            yPos += 22;
            ctx.fillStyle = '#ffd700';
            ctx.fillText(`${this.aiNames[1]}: ${this.ai2Score} (${this.ai2Hand.length} tiles)`, 25, yPos);
            yPos += 22;
        }
        else {
            // 2-player scores
            ctx.font = 'bold 20px Arial';
            ctx.fillStyle = '#00ff00';
            ctx.fillText(`You: ${this.playerScore}`, 25, yPos);
            yPos += 28;
            ctx.fillStyle = '#ff6b6b';
            ctx.fillText(`${this.aiNames[0]}: ${this.aiScore}`, 25, yPos);
            yPos += 25;
            // Tile counts - prominent
            ctx.font = 'bold 15px Arial';
            ctx.fillStyle = '#81c784';
            ctx.fillText(`Your tiles: ${this.playerHand.length}`, 25, yPos);
            ctx.fillStyle = '#ef9a9a';
            ctx.fillText(`${this.aiNames[0]}: ${this.aiHand.length} tiles`, 170, yPos);
            yPos += 22;
        }
        // Board ends info for All Fives
        if ((this.gameMode === 'allfives' || this.gameMode === 'cross') && this.board.length > 0) {
            const endTotal = this.boardLeftEnd + this.boardRightEnd;
            ctx.fillStyle = endTotal % 5 === 0 ? '#ffd700' : 'rgba(255,255,255,0.6)';
            ctx.font = 'bold 14px Arial';
            ctx.fillText(`Ends: ${this.boardLeftEnd} + ${this.boardRightEnd} = ${endTotal}`, 25, yPos);
            if (endTotal % 5 === 0 && endTotal > 0) {
                ctx.fillText('SCORING!', 220, yPos);
            }
            yPos += 20;
        }
        // Time
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = '12px Arial';
        const minutes = Math.floor(this.timeElapsed / 60);
        const seconds = Math.floor(this.timeElapsed % 60);
        ctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')} | Round ${this.roundNumber}`, 25, yPos);
        ctx.restore();
    }
    drawRoundedRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
    }
    drawCombo() {
        if (this.combo <= 1)
            return;
        const ctx = this.ctx;
        ctx.save();
        const scale = 1 + Math.sin(this.animationFrame * 0.2) * 0.1;
        ctx.translate(window.innerWidth / 2, 100);
        ctx.scale(scale, scale);
        // Draw combo background
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 60);
        gradient.addColorStop(0, 'rgba(255, 215, 0, 0.5)');
        gradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(-100, -40, 200, 80);
        // Draw combo text
        ctx.fillStyle = 'white';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${this.combo}x COMBO!`, 0, 0);
        ctx.restore();
    }
    drawParticles() {
        const ctx = this.ctx;
        this.particles.forEach(particle => {
            ctx.save();
            ctx.globalAlpha = particle.life / particle.maxLife;
            if (particle.type === 'emoji') {
                ctx.font = `${particle.size}px Arial`;
                ctx.fillText(particle.emoji, particle.x, particle.y);
            }
            else {
                ctx.fillStyle = particle.color;
                switch (particle.type) {
                    case 'star':
                        this.drawStar(ctx, particle.x, particle.y, particle.size);
                        break;
                    case 'circle':
                        ctx.beginPath();
                        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                        ctx.fill();
                        break;
                    case 'sparkle':
                        this.drawSparkle(ctx, particle.x, particle.y, particle.size);
                        break;
                }
            }
            ctx.restore();
        });
    }
    drawStar(ctx, x, y, size) {
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
            const angle = (i * 72 - 90) * Math.PI / 180;
            const px = x + Math.cos(angle) * size;
            const py = y + Math.sin(angle) * size;
            if (i === 0) {
                ctx.moveTo(px, py);
            }
            else {
                ctx.lineTo(px, py);
            }
            const innerAngle = ((i * 72 + 36) - 90) * Math.PI / 180;
            const ipx = x + Math.cos(innerAngle) * size * 0.5;
            const ipy = y + Math.sin(innerAngle) * size * 0.5;
            ctx.lineTo(ipx, ipy);
        }
        ctx.closePath();
        ctx.fill();
    }
    drawSparkle(ctx, x, y, size) {
        ctx.beginPath();
        ctx.moveTo(x - size, y);
        ctx.lineTo(x + size, y);
        ctx.moveTo(x, y - size);
        ctx.lineTo(x, y + size);
        ctx.lineWidth = 2;
        ctx.strokeStyle = ctx.fillStyle;
        ctx.stroke();
    }
    drawHints() {
        const ctx = this.ctx;
        this.hints.forEach(hint => {
            // Highlight the tile
            const tile = hint.tile;
            ctx.save();
            ctx.strokeStyle = 'rgba(0, 255, 0, 0.8)';
            ctx.lineWidth = 4;
            ctx.strokeRect(tile.x - 35, tile.y - 65, 70, 130);
            // Draw arrow pointing to board position
            ctx.strokeStyle = 'rgba(0, 255, 0, 0.6)';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(tile.x, tile.y);
            const targetX = hint.side === 'left' ? 150 : window.innerWidth - 150;
            const targetY = window.innerHeight / 2 - 50;
            ctx.lineTo(targetX, targetY);
            ctx.stroke();
            ctx.restore();
        });
    }
    createParticles(x, y, type, count, emoji) {
        if (this.prefersReducedMotion)
            return;
        // Cap total particles to prevent memory issues
        if (this.particles.length > 200)
            return;
        for (let i = 0; i < count; i++) {
            this.particles.push({
                x: x + (Math.random() - 0.5) * 50,
                y: y + (Math.random() - 0.5) * 50,
                vx: (Math.random() - 0.5) * 5,
                vy: (Math.random() - 0.5) * 5 - 2,
                life: 2,
                maxLife: 2,
                color: `hsl(${Math.random() * 360}, 100%, 50%)`,
                size: Math.random() * 10 + 5,
                type,
                emoji
            });
        }
    }
    // Double-tap detection state
    lastTapTime = 0;
    lastTapTile = null;
    handleMouseDown(e) {
        if (this.isPaused || !this.gameStarted)
            return;
        if (this.currentPlayerIndex !== 0)
            return; // Not player's turn
        const x = e.clientX;
        const y = e.clientY;
        const now = Date.now();
        // Check if clicking on a tile
        for (const tile of this.playerHand) {
            if (this.isPointInTile(x, y, tile)) {
                // Double-tap detection: auto-place tile
                if (this.lastTapTile === tile && (now - this.lastTapTime) < 400) {
                    this.autoPlaceTile(tile);
                    this.lastTapTile = null;
                    this.lastTapTime = 0;
                    return;
                }
                this.lastTapTile = tile;
                this.lastTapTime = now;
                this.draggedTile = tile;
                this.dragOffset.x = x - tile.x;
                this.dragOffset.y = y - tile.y;
                tile.isDragging = true;
                this.playSound('click');
                break;
            }
        }
        // Check power-ups
        this.powerUps.forEach(powerUp => {
            if (powerUp.active && this.isPointInCircle(x, y, powerUp.x, powerUp.y, 44)) {
                this.usePowerUp(powerUp);
            }
        });
    }
    /**
     * Auto-place a tile on double-tap/click.
     * Finds valid side(s) and plays the tile. If playable on both sides,
     * picks the higher-scoring side for All Fives, or the left side by default.
     */
    autoPlaceTile(tile) {
        const canLeft = this.canPlayOnSide(tile, 'left');
        const canRight = this.canPlayOnSide(tile, 'right');
        if (!canLeft && !canRight) {
            this.playSound('error');
            this.failedAttempts++;
            this.showMessage("That tile can't be played right now", 2000);
            this.checkStruggle();
            tile.isDragging = false;
            this.draggedTile = null;
            return;
        }
        let side;
        if (canLeft && canRight) {
            // Both sides valid - pick the better one for scoring modes
            if (this.gameMode === 'allfives' || this.gameMode === 'cross') {
                // Simulate both and pick higher score
                const leftScore = this.simulateEndScore(tile, 'left');
                const rightScore = this.simulateEndScore(tile, 'right');
                side = rightScore > leftScore ? 'right' : 'left';
            }
            else {
                // Default: first tile goes anywhere, otherwise prefer left
                side = 'left';
            }
        }
        else {
            side = canLeft ? 'left' : 'right';
        }
        tile.isDragging = false;
        this.draggedTile = null;
        this.playTile(tile, side);
        this.lastMoveTime = Date.now();
        this.failedAttempts = 0;
    }
    simulateEndScore(tile, side) {
        let newLeft = this.boardLeftEnd;
        let newRight = this.boardRightEnd;
        if (this.board.length === 0) {
            newLeft = tile.left;
            newRight = tile.right;
        }
        else if (side === 'left') {
            newLeft = tile.left === this.boardLeftEnd ? tile.right : tile.left;
        }
        else {
            newRight = tile.right === this.boardRightEnd ? tile.left : tile.right;
        }
        const total = newLeft + newRight;
        return (total % 5 === 0 && total > 0) ? total : 0;
    }
    handleMouseMove(e) {
        const x = e.clientX;
        const y = e.clientY;
        // Update hover state
        this.playerHand.forEach(tile => {
            tile.isHovered = this.isPointInTile(x, y, tile);
        });
        // Handle dragging
        if (this.draggedTile) {
            this.draggedTile.x = x - this.dragOffset.x;
            this.draggedTile.y = y - this.dragOffset.y;
            // Check valid drop zones
            this.validDropZone = this.getValidDropZone(x, y);
        }
    }
    handleMouseUp(e) {
        if (this.isPaused)
            return;
        if (this.draggedTile && this.validDropZone) {
            this.playTile(this.draggedTile, this.validDropZone);
            this.lastMoveTime = Date.now();
        }
        else if (this.draggedTile) {
            // Invalid move attempt - tile was dragged but not to a valid drop zone
            if (this.draggedTile.y < window.innerHeight - 200) {
                this.failedAttempts++;
                this.playSound('error');
                this.checkStruggle();
            }
            // Return tile to hand
            this.draggedTile.isDragging = false;
            this.arrangePlayerHand();
        }
        this.draggedTile = null;
        this.validDropZone = null;
    }
    /**
     * Check if the player is struggling based on failed attempts only.
     * Never auto-triggers from inactivity - respects player's time.
     */
    checkStruggle() {
        if (this.failedAttempts < 3)
            return;
        // Find all valid moves
        const validMoves = [];
        for (const tile of this.playerHand) {
            if (this.canPlayOnSide(tile, 'left'))
                validMoves.push({ tile, side: 'left' });
            if (this.canPlayOnSide(tile, 'right'))
                validMoves.push({ tile, side: 'right' });
        }
        if (validMoves.length === 0) {
            // No valid moves - guide player to draw or pass
            const rules = getGameRules(this.gameMode);
            if (rules.canDraw && this.tiles.length > 0) {
                this.showMessage('No matching tiles! Draw from the pile.', 4000);
                this.showDrawButton();
            }
            else {
                this.showMessage('No valid moves - you need to pass.', 4000);
                this.showPassButton();
            }
            this.failedAttempts = 0;
            return;
        }
        // Has valid moves - show the help banner
        this.showStruggleHelp(validMoves);
        this.failedAttempts = 0;
    }
    showStruggleHelp(validMoves) {
        // Remove existing help banner
        document.getElementById('struggleHelp')?.remove();
        // Highlight valid tiles with glow
        this.hints = validMoves;
        this.showHint = true;
        const banner = document.createElement('div');
        banner.id = 'struggleHelp';
        banner.style.cssText = `
      position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
      background: rgba(0,0,0,0.85); backdrop-filter: blur(10px);
      border: 1px solid rgba(255,215,0,0.4); border-radius: 16px;
      padding: 16px 24px; max-width: 400px; width: 90%;
      z-index: 3000; text-align: center;
      animation: fadeIn 0.3s ease-out;
    `;
        const rules = getGameRules(this.gameMode);
        const matchInfo = this.board.length === 0
            ? 'Place any tile to start the game!'
            : `Match the <b style="color:#ffd700;">${this.boardLeftEnd}</b> on the left or <b style="color:#ffd700;">${this.boardRightEnd}</b> on the right end.`;
        banner.innerHTML = `
      <div style="color: #ffd700; font-size: 14px; font-weight: 600; margin-bottom: 6px;">
        Need help? ${validMoves.length} tile${validMoves.length > 1 ? 's' : ''} can be played!
      </div>
      <div style="color: #e2e8f0; font-size: 13px; line-height: 1.5; margin-bottom: 12px;">
        ${matchInfo}<br>
        <span style="color: #81c784;">Playable tiles are glowing green.</span>
        Double-tap a tile to auto-place it.
      </div>
      <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
        <button id="helpDismiss" style="
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2);
          color: white; padding: 8px 20px; border-radius: 20px;
          cursor: pointer; font-size: 13px;
        ">Got it</button>
        <button id="helpRules" style="
          background: rgba(255,215,0,0.2); border: 1px solid rgba(255,215,0,0.4);
          color: #ffd700; padding: 8px 20px; border-radius: 20px;
          cursor: pointer; font-size: 13px;
        ">How to Play ${rules.name}</button>
      </div>
    `;
        document.body.appendChild(banner);
        document.getElementById('helpDismiss').onclick = () => {
            banner.remove();
            // Keep hints glowing for a bit
            setTimeout(() => { this.showHint = false; this.hints = []; }, 8000);
        };
        document.getElementById('helpRules').onclick = () => {
            banner.remove();
            this.showHint = false;
            this.hints = [];
            this.showSelectedModeRules();
        };
        // Auto-dismiss after 15 seconds
        setTimeout(() => {
            banner.remove();
            this.showHint = false;
            this.hints = [];
        }, 15000);
    }
    autoShowHint() {
        this.checkStruggle();
    }
    handleTouchStart(e) {
        e.preventDefault();
        const touch = e.touches[0];
        this.handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY });
    }
    handleTouchMove(e) {
        e.preventDefault();
        const touch = e.touches[0];
        this.handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    }
    handleTouchEnd(e) {
        e.preventDefault();
        this.handleMouseUp({});
    }
    handleWheel(e) {
        // Could implement zoom or other wheel-based interactions
    }
    handleKeyDown(e) {
        switch (e.key) {
            case 'Escape':
                if (this.gameStarted) {
                    this.togglePause();
                }
                break;
            case 'h':
                if (this.gameStarted)
                    this.toggleHints();
                break;
            case 's':
                this.toggleSound();
                break;
        }
    }
    handleKeyUp(e) {
        // Handle key release events if needed
    }
    isPointInTile(x, y, tile) {
        // Expanded hit area for touch accuracy (min 44px per side)
        const padX = 36;
        const padY = 66;
        return x >= tile.x - padX && x <= tile.x + padX &&
            y >= tile.y - padY && y <= tile.y + padY;
    }
    isPointInCircle(x, y, cx, cy, radius) {
        const dx = x - cx;
        const dy = y - cy;
        return Math.sqrt(dx * dx + dy * dy) <= radius;
    }
    getValidDropZone(x, y) {
        const boardY = window.innerHeight / 2 - 50;
        if (y >= boardY - 100 && y <= boardY + 100) {
            if (x < window.innerWidth / 2) {
                return this.canPlayOnSide(this.draggedTile, 'left') ? 'left' : null;
            }
            else {
                return this.canPlayOnSide(this.draggedTile, 'right') ? 'right' : null;
            }
        }
        return null;
    }
    canPlayOnSide(tile, side) {
        // First tile rules
        if (this.board.length === 0) {
            const rules = getGameRules(this.gameMode);
            // Some modes require starting with a specific tile
            if (rules.startingTile === 'double') {
                return tile.left === tile.right; // Must start with a double
            }
            // Six-Love traditionally starts with double-six
            if (this.gameMode === 'sixlove') {
                return tile.left === 6 && tile.right === 6;
            }
            return true;
        }
        // Mode-specific placement rules
        if (this.gameMode === 'cross' && this.board.length < 4) {
            // Cross mode: first double creates spinner, then must play on all 4 sides
            // Simplified for 2-player
            const boardEnd = side === 'left' ? this.boardLeftEnd : this.boardRightEnd;
            return tile.left === boardEnd || tile.right === boardEnd;
        }
        // Block mode: stricter matching
        if (this.gameMode === 'block') {
            const boardEnd = side === 'left' ? this.boardLeftEnd : this.boardRightEnd;
            // In block mode, you must match exactly
            return tile.left === boardEnd || tile.right === boardEnd;
        }
        // Standard matching for other modes
        const boardEnd = side === 'left' ? this.boardLeftEnd : this.boardRightEnd;
        return tile.left === boardEnd || tile.right === boardEnd;
    }
    playTile(tile, side) {
        // Remove pass button if exists
        const passBtn = document.getElementById('passButton');
        if (passBtn)
            passBtn.remove();
        // Remove from hand
        const index = this.playerHand.indexOf(tile);
        if (index > -1) {
            this.playerHand.splice(index, 1);
        }
        // Add to board
        if (this.board.length === 0) {
            this.board.push(tile);
            this.boardLeftEnd = tile.left;
            this.boardRightEnd = tile.right;
        }
        else {
            if (side === 'left') {
                this.board.unshift(tile);
                this.boardLeftEnd = tile.left === this.boardLeftEnd ? tile.right : tile.left;
            }
            else {
                this.board.push(tile);
                this.boardRightEnd = tile.right === this.boardRightEnd ? tile.left : tile.right;
            }
        }
        // Update moves counter
        this.movesCount++;
        // Update score
        this.updateScore(tile);
        // Domino slam effect for doubles or winning moves
        const isDouble = tile.left === tile.right;
        const isWinningMove = this.playerHand.length === 0;
        if (isDouble || isWinningMove) {
            // SLAM IT!
            this.playSound('slam');
            this.screenShake = 10;
            // Create impact particles
            for (let i = 0; i < 20; i++) {
                this.particles.push({
                    x: window.innerWidth / 2,
                    y: window.innerHeight / 2,
                    vx: Math.random() * 10 - 5,
                    vy: Math.random() * -10 - 5,
                    life: 1,
                    maxLife: 1,
                    color: '#FFD700',
                    size: Math.random() * 5 + 3,
                    type: 'star'
                });
            }
        }
        else {
            // Normal placement
            this.playSound('place');
            this.screenShake = 3;
        }
        // Haptic feedback
        this.triggerHaptic(isDouble ? 'heavy' : 'light');
        // Check win - player dominoed (emptied hand)
        if (this.playerHand.length === 0) {
            this.awardRoundScore(0); // player index 0 wins
            this.triggerHaptic('success');
            this.playerWins();
            return;
        }
        // Last tile warning for player
        if (this.playerHand.length === 1) {
            this.showLastTileWarning('You');
        }
        // Rearrange hand
        this.arrangePlayerHand();
        this.consecutivePasses = 0;
        // Advance to next player's turn
        this.advanceTurn();
        this.scheduleNextTurn();
    }
    updateScore(tile) {
        const rules = getGameRules(this.gameMode);
        let pointsEarned = 0;
        switch (this.gameMode) {
            case 'allfives':
                // Score when board ends total to multiple of 5
                const total = this.boardLeftEnd + this.boardRightEnd;
                if (total % 5 === 0 && total > 0) {
                    pointsEarned = total;
                    this.combo++;
                    this.maxCombo = Math.max(this.maxCombo, this.combo);
                }
                else {
                    this.combo = 0;
                }
                break;
            case 'cross':
                // Score points from all 4 ends when multiple of 5
                // For now, simplified to 2 ends
                const crossTotal = this.boardLeftEnd + this.boardRightEnd;
                if (crossTotal % 5 === 0 && crossTotal > 0) {
                    pointsEarned = crossTotal;
                }
                break;
            case 'classic':
            case 'block':
            case 'draw':
                // Points scored at end of round
                // But show pip advantage in real-time
                const playerPips = this.playerHand.reduce((sum, t) => sum + t.left + t.right, 0);
                const aiPips = this.aiHand.reduce((sum, t) => sum + t.left + t.right, 0);
                if (aiPips > playerPips) {
                    // Show pip advantage
                    this.showPipAdvantage(aiPips - playerPips);
                }
                break;
            case 'partner':
            case 'sixlove':
            case 'cutthroat':
                // These modes score at end of round based on opponent pips, not during play
                break;
        }
        // Apply score if earned
        if (pointsEarned > 0) {
            this.playerScore += pointsEarned;
            // Show score popup with animation
            this.showScorePopup(pointsEarned);
            // Play sound based on score size
            if (pointsEarned >= 20) {
                this.playSound('combo');
            }
            else {
                this.playSound('powerup');
            }
            // Create celebratory particles
            this.createScoreParticles(pointsEarned);
        }
        // Update score display immediately
        this.updateScoreDisplay();
    }
    /**
     * Award end-of-round score when a player dominoes (empties hand).
     * The winner scores the sum of all opponents' remaining pips.
     * For team modes, the winning team scores the losing team's pips.
     */
    awardRoundScore(winnerIndex) {
        const rules = getGameRules(this.gameMode);
        // All Fives / Cross already score during play, but also add end-of-round pips
        // Classic / Block / Draw / Partner / Cutthroat / Six-Love score at round end
        const hands = [this.playerHand, this.aiHand, this.ai2Hand, this.ai3Hand];
        const pipCounts = hands.map(h => h.reduce((sum, t) => sum + t.left + t.right, 0));
        if (rules.teamPlay) {
            // Team mode: winning team scores losing team's total pips
            const winnerTeam = this.getPlayerTeam(winnerIndex);
            let losingPips = 0;
            for (let i = 0; i < this.playerCount; i++) {
                if (this.getPlayerTeam(i) !== winnerTeam) {
                    losingPips += pipCounts[i];
                }
            }
            if (winnerTeam === 1) {
                this.playerScore += losingPips;
            }
            else {
                this.aiScore += losingPips;
            }
        }
        else if (this.playerCount === 3) {
            // 3-player: winner scores sum of both opponents' pips
            const totalOpponentPips = pipCounts.reduce((s, p) => s + p, 0) - pipCounts[winnerIndex];
            if (winnerIndex === 0) {
                this.playerScore += totalOpponentPips;
            }
            else {
                this.aiScore += totalOpponentPips;
            }
        }
        else {
            // 2-player: winner scores opponent's remaining pips
            if (winnerIndex === 0) {
                this.playerScore += pipCounts[1]; // AI's remaining pips
            }
            else {
                this.aiScore += pipCounts[0]; // Player's remaining pips
            }
        }
    }
    showAIScorePopup(points) {
        const popup = document.createElement('div');
        popup.style.cssText = `
      position: fixed;
      top: 30%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 36px;
      font-weight: bold;
      color: #ff6b6b;
      text-shadow: 0 0 20px rgba(255, 107, 107, 0.8);
      animation: scorePopup 1s ease-out;
      z-index: 1000;
      pointer-events: none;
    `;
        popup.textContent = `AI +${points}`;
        document.body.appendChild(popup);
        setTimeout(() => popup.remove(), 1000);
    }
    showPipAdvantage(advantage) {
        const msg = document.createElement('div');
        msg.style.cssText = `
      position: fixed;
      bottom: 180px;
      right: 20px;
      background: rgba(0, 255, 0, 0.2);
      color: #00ff00;
      padding: 10px 20px;
      border-radius: 10px;
      font-size: 16px;
      z-index: 500;
      animation: fadeInOut 2s ease-out;
    `;
        msg.innerHTML = `Pip Advantage: +${advantage}`;
        document.body.appendChild(msg);
        setTimeout(() => msg.remove(), 2000);
    }
    /**
     * Animated banner showing AI's move - tile slides in from the side.
     */
    showAIMoveBanner(playerName, tile, side) {
        document.getElementById('aiMoveBanner')?.remove();
        const banner = document.createElement('div');
        banner.id = 'aiMoveBanner';
        banner.style.cssText = `
      position: fixed; top: 100px; left: 50%; transform: translateX(-50%) translateY(-20px);
      background: rgba(0,0,0,0.85); backdrop-filter: blur(8px);
      border: 1px solid rgba(255,107,107,0.4); border-radius: 14px;
      padding: 12px 24px; z-index: 2500; text-align: center;
      opacity: 0; transition: all 0.3s ease-out;
    `;
        const isDouble = tile.left === tile.right;
        banner.innerHTML = `
      <div style="color: rgba(255,255,255,0.6); font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">${playerName}</div>
      <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 4px;">
        <div style="
          display: flex; background: white; border-radius: 6px; overflow: hidden;
          border: 2px solid ${isDouble ? '#ffd700' : '#999'}; box-shadow: 0 2px 12px rgba(0,0,0,0.4);
          ${isDouble ? 'animation: pulse 0.5s ease-in-out;' : ''}
        ">
          <div style="padding: 6px 10px; font-size: 18px; font-weight: bold; color: #333; border-right: 1px solid #ccc;">${tile.left}</div>
          <div style="padding: 6px 10px; font-size: 18px; font-weight: bold; color: #333;">${tile.right}</div>
        </div>
        <div style="color: rgba(255,255,255,0.5); font-size: 12px;">
          ${this.board.length <= 1 ? 'starts' : `plays ${side}`}
        </div>
      </div>
    `;
        document.body.appendChild(banner);
        // Animate in
        requestAnimationFrame(() => {
            banner.style.opacity = '1';
            banner.style.transform = 'translateX(-50%) translateY(0)';
        });
        // Animate out
        setTimeout(() => {
            banner.style.opacity = '0';
            banner.style.transform = 'translateX(-50%) translateY(-20px)';
            setTimeout(() => banner.remove(), 300);
        }, 1500);
    }
    /**
     * Dramatic "Last Tile!" warning when a player is down to 1 tile.
     */
    showLastTileWarning(playerName) {
        document.getElementById('lastTileWarning')?.remove();
        const isPlayer = playerName === 'You';
        const warning = document.createElement('div');
        warning.id = 'lastTileWarning';
        warning.style.cssText = `
      position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.5);
      z-index: 3000; text-align: center; pointer-events: none;
      opacity: 0; transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    `;
        warning.innerHTML = `
      <div style="
        font-size: clamp(28px, 6vw, 48px); font-weight: 900; color: ${isPlayer ? '#ffd700' : '#ff6b6b'};
        text-shadow: 0 0 20px ${isPlayer ? 'rgba(255,215,0,0.5)' : 'rgba(255,107,107,0.5)'};
      ">LAST TILE!</div>
      <div style="color: white; font-size: 16px; opacity: 0.8; margin-top: 4px;">
        ${isPlayer ? 'One more to win!' : `${playerName} has one tile left!`}
      </div>
    `;
        document.body.appendChild(warning);
        this.playSound('powerup');
        this.screenShake = 5;
        this.triggerHaptic('warning');
        // Animate in
        requestAnimationFrame(() => {
            warning.style.opacity = '1';
            warning.style.transform = 'translate(-50%, -50%) scale(1)';
        });
        // Animate out
        setTimeout(() => {
            warning.style.opacity = '0';
            warning.style.transform = 'translate(-50%, -50%) scale(1.2)';
            setTimeout(() => warning.remove(), 400);
        }, 2000);
    }
    /**
     * Haptic feedback via Capacitor - gracefully degrades on web.
     */
    async triggerHaptic(style) {
        try {
            const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
            switch (style) {
                case 'light':
                    await Haptics.impact({ style: ImpactStyle.Light });
                    break;
                case 'medium':
                    await Haptics.impact({ style: ImpactStyle.Medium });
                    break;
                case 'heavy':
                    await Haptics.impact({ style: ImpactStyle.Heavy });
                    break;
                case 'success':
                    await Haptics.notification({ type: NotificationType.Success });
                    break;
                case 'warning':
                    await Haptics.notification({ type: NotificationType.Warning });
                    break;
                case 'error':
                    await Haptics.notification({ type: NotificationType.Error });
                    break;
            }
        }
        catch {
            // Not on a native device or haptics unavailable - silently ignore
        }
    }
    createScoreParticles(points) {
        const particleCount = Math.min(points, 30);
        for (let i = 0; i < particleCount; i++) {
            this.particles.push({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
                vx: (Math.random() - 0.5) * 8,
                vy: Math.random() * -8 - 2,
                life: 1,
                maxLife: 1,
                color: '#FFD700',
                size: Math.random() * 4 + 2,
                type: 'star'
            });
        }
    }
    updateScoreDisplay() {
        // Force immediate visual update of score
        const ctx = this.ctx;
        if (ctx) {
            this.drawScore();
        }
    }
    showScorePopup(points) {
        const popup = document.createElement('div');
        popup.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 48px;
      font-weight: bold;
      color: #ffd700;
      text-shadow: 0 0 20px rgba(255, 215, 0, 0.8);
      animation: scorePopup 1s ease-out;
      z-index: 1000;
      pointer-events: none;
    `;
        popup.textContent = `+${points}`;
        document.body.appendChild(popup);
        setTimeout(() => popup.remove(), 1000);
    }
    aiTurn() {
        // Legacy 2-player: delegate to indexed version
        this.aiTurnForPlayer(1);
    }
    aiTurnForPlayer(playerIndex) {
        if (!this.gameStarted || this.isPaused)
            return;
        const hand = this.getHandForPlayer(playerIndex);
        const playerName = this.getPlayerName(playerIndex);
        // Find valid moves for this AI
        const validMoves = [];
        for (const tile of hand) {
            if (this.canPlayOnSide(tile, 'left')) {
                validMoves.push({ tile, side: 'left' });
            }
            if (this.canPlayOnSide(tile, 'right')) {
                validMoves.push({ tile, side: 'right' });
            }
        }
        if (validMoves.length > 0) {
            this.consecutivePasses = 0;
            // AI strategy based on difficulty and variant
            const chosenMove = this.chooseAIMove(validMoves, playerIndex);
            // Remove tile from hand
            const idx = hand.indexOf(chosenMove.tile);
            hand.splice(idx, 1);
            // Place tile on board
            if (this.board.length === 0) {
                this.board.push(chosenMove.tile);
                this.boardLeftEnd = chosenMove.tile.left;
                this.boardRightEnd = chosenMove.tile.right;
            }
            else {
                if (chosenMove.side === 'left') {
                    this.board.unshift(chosenMove.tile);
                    this.boardLeftEnd = chosenMove.tile.left === this.boardLeftEnd ?
                        chosenMove.tile.right : chosenMove.tile.left;
                }
                else {
                    this.board.push(chosenMove.tile);
                    this.boardRightEnd = chosenMove.tile.right === this.boardRightEnd ?
                        chosenMove.tile.left : chosenMove.tile.right;
                }
            }
            // Calculate score for this AI
            this.updateAIScoreForPlayer(chosenMove.tile, playerIndex);
            // Slam effect for doubles
            if (chosenMove.tile.left === chosenMove.tile.right) {
                this.playSound('slam');
                this.screenShake = 6;
                this.triggerHaptic('heavy');
            }
            else {
                this.playSound('place');
                this.triggerHaptic('light');
            }
            // Show who played with animated banner
            this.showAIMoveBanner(playerName, chosenMove.tile, chosenMove.side);
            // Last tile warning
            if (hand.length === 1) {
                setTimeout(() => {
                    this.showLastTileWarning(playerName);
                }, 800);
            }
            // Check if this AI won (emptied hand)
            if (hand.length === 0) {
                this.awardRoundScore(playerIndex);
                if (playerIndex === 0) {
                    this.playerWins();
                }
                else {
                    this.handleAIWin(playerIndex);
                }
                return;
            }
        }
        else {
            // AI passes
            this.consecutivePasses++;
            this.showMessage(`${playerName} passes`, 1000);
            // Check if all players have passed (game blocked)
            if (this.consecutivePasses >= this.playerCount) {
                this.handleBlockedGame();
                return;
            }
        }
        // Advance to next player's turn
        this.advanceTurn();
        this.scheduleNextTurn();
    }
    scheduleNextTurn() {
        if (!this.gameStarted)
            return;
        if (this.currentPlayerIndex === 0) {
            // Player's turn - check if they can move
            if (!this.canPlayerMove()) {
                if (this.gameMode === 'draw' && this.tiles.length > 0) {
                    this.showDrawButton();
                }
                else {
                    this.showPassButton();
                }
            }
        }
        else {
            // AI turn with thinking delay
            const thinkingTime = this.getAIThinkingTime();
            this.pendingAITimeout = window.setTimeout(() => this.aiTurnForPlayer(this.currentPlayerIndex), thinkingTime);
        }
    }
    getAIThinkingTime() {
        switch (this.difficulty) {
            case 'easy': return 600 + Math.random() * 400;
            case 'medium': return 800 + Math.random() * 600;
            case 'hard': return 1000 + Math.random() * 800;
            case 'expert': return 1200 + Math.random() * 1000;
            default: return 800;
        }
    }
    chooseAIMove(moves, playerIndex) {
        if (this.difficulty === 'easy') {
            // Easy: mostly random, slight preference for high-value tiles
            if (Math.random() < 0.7) {
                return moves[Math.floor(Math.random() * moves.length)];
            }
        }
        if (this.difficulty === 'medium') {
            // Medium: mix of random and tactical
            if (Math.random() < 0.4) {
                return moves[Math.floor(Math.random() * moves.length)];
            }
        }
        // Hard/Expert: variant-aware strategy
        return this.chooseBestMoveForVariant(moves, playerIndex);
    }
    chooseBestMoveForVariant(moves, playerIndex) {
        const hand = this.getHandForPlayer(playerIndex);
        let bestMove = moves[0];
        let bestScore = -Infinity;
        for (const move of moves) {
            let score = 0;
            // Simulate board ends after this move
            let newLeftEnd = this.boardLeftEnd;
            let newRightEnd = this.boardRightEnd;
            if (this.board.length === 0) {
                newLeftEnd = move.tile.left;
                newRightEnd = move.tile.right;
            }
            else if (move.side === 'left') {
                newLeftEnd = move.tile.left === this.boardLeftEnd ? move.tile.right : move.tile.left;
            }
            else {
                newRightEnd = move.tile.right === this.boardRightEnd ? move.tile.left : move.tile.right;
            }
            // All Fives: prioritize scoring moves
            if (this.gameMode === 'allfives' || this.gameMode === 'cross') {
                const endTotal = newLeftEnd + newRightEnd;
                if (endTotal % 5 === 0 && endTotal > 0) {
                    score += endTotal * 3; // Heavy weight on scoring
                }
            }
            // Block/Classic: prefer moves that reduce pip count
            if (this.gameMode === 'block' || this.gameMode === 'classic') {
                score += (move.tile.left + move.tile.right); // Play high-pip tiles first
            }
            // Prefer doubles early (get rid of them)
            if (move.tile.left === move.tile.right) {
                score += 5;
            }
            // Expert: blocking consideration
            if (this.difficulty === 'expert') {
                // Count how many of our remaining tiles match the new ends
                const remainingHand = hand.filter(t => t !== move.tile);
                const futurePlayable = remainingHand.filter(t => t.left === newLeftEnd || t.right === newLeftEnd ||
                    t.left === newRightEnd || t.right === newRightEnd).length;
                score += futurePlayable * 2; // Keep ourselves playable
                // Partner awareness: avoid blocking partner
                if ((this.gameMode === 'partner' || this.gameMode === 'sixlove') &&
                    this.getPlayerTeam(playerIndex) === this.getPlayerTeam(0)) {
                    // Try not to block the human player (our partner)
                    const partnerCanPlay = this.playerHand.some(t => t.left === newLeftEnd || t.right === newLeftEnd ||
                        t.left === newRightEnd || t.right === newRightEnd);
                    if (!partnerCanPlay)
                        score -= 10;
                }
            }
            // Cutthroat: play defensively against both opponents
            if (this.gameMode === 'cutthroat' && (this.difficulty === 'hard' || this.difficulty === 'expert')) {
                // Prefer moves that limit opponents' options
                score += (move.tile.left + move.tile.right) * 0.5;
            }
            if (score > bestScore) {
                bestScore = score;
                bestMove = move;
            }
        }
        return bestMove;
    }
    handleAIWin(winnerIndex) {
        this.gameStarted = false;
        const rules = getGameRules(this.gameMode);
        const isTeamMode = rules.teamPlay;
        if (isTeamMode) {
            const winnerTeam = this.getPlayerTeam(winnerIndex);
            if (winnerTeam === 1) {
                // Player's team wins (player + AI partner)
                this.playerWins();
            }
            else {
                this.aiWins();
            }
        }
        else {
            this.aiWins();
        }
    }
    handleBlockedGame() {
        this.gameStarted = false;
        const rules = getGameRules(this.gameMode);
        const isTeamMode = rules.teamPlay;
        if (isTeamMode) {
            // Team mode: compare team pip totals
            const team1Pips = this.playerHand.reduce((s, t) => s + t.left + t.right, 0) +
                this.ai2Hand.reduce((s, t) => s + t.left + t.right, 0);
            const team2Pips = this.aiHand.reduce((s, t) => s + t.left + t.right, 0) +
                this.ai3Hand.reduce((s, t) => s + t.left + t.right, 0);
            if (team1Pips < team2Pips) {
                this.playerScore += (team2Pips - team1Pips);
                this.playerWins(true);
            }
            else if (team2Pips < team1Pips) {
                this.aiScore += (team1Pips - team2Pips);
                this.aiWins(true);
            }
            else {
                this.gameDraw();
            }
        }
        else if (this.playerCount === 3) {
            // 3-player: lowest individual pip count wins
            const pips = [
                this.playerHand.reduce((s, t) => s + t.left + t.right, 0),
                this.aiHand.reduce((s, t) => s + t.left + t.right, 0),
                this.ai2Hand.reduce((s, t) => s + t.left + t.right, 0),
            ];
            const minPips = Math.min(...pips);
            const winnerIdx = pips.indexOf(minPips);
            const totalOtherPips = pips.reduce((s, p) => s + p, 0) - minPips;
            if (winnerIdx === 0) {
                this.playerScore += totalOtherPips;
                this.playerWins(true);
            }
            else {
                this.aiScore += totalOtherPips;
                this.aiWins(true);
            }
        }
        else {
            // 2-player blocked game (existing logic)
            const playerPips = this.playerHand.reduce((sum, tile) => sum + tile.left + tile.right, 0);
            const aiPips = this.aiHand.reduce((sum, tile) => sum + tile.left + tile.right, 0);
            if (playerPips < aiPips) {
                this.playerScore += (aiPips - playerPips);
                this.playerWins(true);
            }
            else if (aiPips < playerPips) {
                this.aiScore += (playerPips - aiPips);
                this.aiWins(true);
            }
            else {
                this.gameDraw();
            }
        }
    }
    showDrawButton() {
        if (document.getElementById('drawButton'))
            return;
        const drawBtn = document.createElement('button');
        drawBtn.id = 'drawButton';
        drawBtn.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span style="font-size: 36px; margin-bottom: 5px;">📥</span>
        <span style="font-size: 20px; font-weight: bold;">DRAW TILE</span>
        <span style="font-size: 14px; opacity: 0.8;">(${this.tiles.length} remaining)</span>
      </div>
    `;
        drawBtn.style.cssText = `
      position: fixed;
      bottom: 250px;
      left: 50%;
      transform: translateX(-50%);
      padding: 20px 40px;
      background: linear-gradient(135deg, #4ecdc4 0%, #44b0a8 100%);
      border: 3px solid white;
      color: white;
      border-radius: 20px;
      cursor: pointer;
      box-shadow: 0 10px 30px rgba(78, 205, 196, 0.5);
      animation: pulseGlow 1.5s infinite;
      z-index: 2000;
    `;
        drawBtn.addEventListener('click', () => {
            this.playSound('click');
            if (this.tiles.length > 0) {
                const drawnTile = this.tiles.pop();
                this.playerHand.push(drawnTile);
                this.arrangePlayerHand();
                this.showMessage(`Drew [${drawnTile.left}|${drawnTile.right}]`, 1500);
                // Check if drawn tile is playable
                if (this.canPlayOnSide(drawnTile, 'left') || this.canPlayOnSide(drawnTile, 'right')) {
                    drawBtn.remove();
                    this.showMessage('You can play the drawn tile!', 2000);
                }
                else if (this.tiles.length > 0 && this.gameMode === 'draw') {
                    // Must keep drawing in Draw mode
                    const countEl = drawBtn.querySelector('span:last-child');
                    if (countEl)
                        countEl.textContent = `(${this.tiles.length} remaining)`;
                }
                else {
                    drawBtn.remove();
                    if (!this.canPlayerMove()) {
                        this.showPassButton();
                    }
                }
            }
            else {
                drawBtn.remove();
                this.showPassButton();
            }
        });
        document.body.appendChild(drawBtn);
    }
    updateAIScoreForPlayer(tile, playerIndex) {
        const rules = getGameRules(this.gameMode);
        let pointsEarned = 0;
        switch (this.gameMode) {
            case 'allfives': {
                const total = this.boardLeftEnd + this.boardRightEnd;
                if (total % 5 === 0 && total > 0)
                    pointsEarned = total;
                break;
            }
            case 'cross': {
                const crossTotal = this.boardLeftEnd + this.boardRightEnd;
                if (crossTotal % 5 === 0 && crossTotal > 0)
                    pointsEarned = crossTotal;
                break;
            }
            case 'partner':
            case 'sixlove':
            case 'cutthroat':
                // These modes score at end of round, not during play
                break;
        }
        if (pointsEarned > 0) {
            if (rules.teamPlay) {
                const team = this.getPlayerTeam(playerIndex);
                if (team === 1) {
                    this.playerScore += pointsEarned;
                }
                else {
                    this.aiScore += pointsEarned;
                }
            }
            else if (playerIndex === 2) {
                this.ai2Score += pointsEarned;
            }
            else {
                this.aiScore += pointsEarned;
            }
            this.showAIScorePopup(pointsEarned);
        }
    }
    canPlayerMove() {
        if (this.board.length === 0)
            return true;
        for (const tile of this.playerHand) {
            if (this.canPlayOnSide(tile, 'left') || this.canPlayOnSide(tile, 'right')) {
                return true;
            }
        }
        return false;
    }
    canAIMove() {
        if (this.board.length === 0)
            return true;
        for (const tile of this.aiHand) {
            if (this.canPlayOnSide(tile, 'left') || this.canPlayOnSide(tile, 'right')) {
                return true;
            }
        }
        return false;
    }
    checkForGameEnd() {
        // Check if ALL players are blocked
        const allBlocked = this.areAllPlayersBlocked();
        if (allBlocked) {
            this.handleBlockedGame();
        }
        else if (this.currentPlayerIndex === 0 && !this.canPlayerMove()) {
            if (this.gameMode === 'draw' && this.tiles.length > 0) {
                this.showDrawButton();
            }
            else {
                this.showPassButton();
            }
        }
    }
    areAllPlayersBlocked() {
        const hands = [this.playerHand, this.aiHand, this.ai2Hand, this.ai3Hand]
            .slice(0, this.playerCount);
        for (const hand of hands) {
            for (const tile of hand) {
                if (this.canPlayOnSide(tile, 'left') || this.canPlayOnSide(tile, 'right')) {
                    return false;
                }
            }
        }
        return true;
    }
    showPassButton() {
        // Check if pass button already exists
        if (document.getElementById('passButton'))
            return;
        const passBtn = document.createElement('button');
        passBtn.id = 'passButton';
        passBtn.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span style="font-size: 36px; margin-bottom: 5px;">🚫</span>
        <span style="font-size: 24px; font-weight: bold;">PASS TURN</span>
        <span style="font-size: 14px; opacity: 0.8;">(No Valid Moves)</span>
      </div>
    `;
        passBtn.style.cssText = `
      position: fixed;
      bottom: 250px;
      left: 50%;
      transform: translateX(-50%);
      padding: 25px 50px;
      background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
      border: 3px solid white;
      color: white;
      border-radius: 20px;
      cursor: pointer;
      box-shadow: 0 15px 40px rgba(255, 107, 107, 0.6);
      animation: pulseGlow 1.5s infinite;
      z-index: 2000;
      transition: transform 0.2s;
    `;
        // Add CSS animation for the glow effect
        if (!document.getElementById('passButtonStyles')) {
            const style = document.createElement('style');
            style.id = 'passButtonStyles';
            style.innerHTML = `
        @keyframes pulseGlow {
          0% { transform: translateX(-50%) scale(1); box-shadow: 0 15px 40px rgba(255, 107, 107, 0.6); }
          50% { transform: translateX(-50%) scale(1.05); box-shadow: 0 20px 50px rgba(255, 107, 107, 0.8); }
          100% { transform: translateX(-50%) scale(1); box-shadow: 0 15px 40px rgba(255, 107, 107, 0.6); }
        }
      `;
            document.head.appendChild(style);
        }
        passBtn.addEventListener('mouseenter', () => {
            passBtn.style.transform = 'translateX(-50%) scale(1.1)';
        });
        passBtn.addEventListener('mouseleave', () => {
            passBtn.style.transform = 'translateX(-50%) scale(1)';
        });
        passBtn.addEventListener('click', () => {
            this.playSound('click');
            // Animate button disappearing
            passBtn.style.animation = 'fadeOut 0.3s ease-out';
            passBtn.style.opacity = '0';
            setTimeout(() => {
                passBtn.remove();
            }, 300);
            // Show passing message
            this.showMessage('Passing turn...', 1200);
            this.consecutivePasses++;
            this.movesCount++;
            // Check if all players have now passed
            if (this.consecutivePasses >= this.playerCount) {
                setTimeout(() => this.handleBlockedGame(), 800);
                return;
            }
            // Advance to next player
            this.advanceTurn();
            setTimeout(() => this.scheduleNextTurn(), 1000);
        });
        document.body.appendChild(passBtn);
        // Show prominent message
        const msgBox = document.createElement('div');
        msgBox.style.cssText = `
      position: fixed;
      top: 30%;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(255, 0, 0, 0.9);
      color: white;
      padding: 20px 40px;
      font-size: 24px;
      font-weight: bold;
      border-radius: 15px;
      z-index: 1999;
      animation: slideDown 0.5s ease-out;
    `;
        msgBox.innerHTML = '⚠️ No Valid Moves Available!';
        document.body.appendChild(msgBox);
        setTimeout(() => {
            msgBox.style.animation = 'fadeOut 0.5s ease-out';
            setTimeout(() => msgBox.remove(), 500);
        }, 3000);
    }
    showMessage(text, duration = 2000) {
        // Remove existing messages
        const existing = document.querySelector('[style*="transform: translate(-50%, -50%)"]');
        if (existing && !existing.textContent?.includes('Paused'))
            existing.remove();
        const msg = document.createElement('div');
        msg.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 20px 40px;
      font-size: 24px;
      border-radius: 10px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      z-index: 2000;
      animation: fadeIn 0.3s ease-in;
    `;
        msg.textContent = text;
        document.body.appendChild(msg);
        if (duration > 0) {
            setTimeout(() => msg.remove(), duration);
        }
    }
    playerWins(blocked = false) {
        this.gameStarted = false;
        this.playSound('win');
        // Record match in history
        this.authManager.recordMatch({
            variant: this.gameMode,
            difficulty: this.difficulty,
            playerScore: this.playerScore,
            opponentScore: this.aiScore,
            won: true,
            blocked,
            moveCount: this.movesCount,
            duration: Math.floor(this.timeElapsed),
            xpEarned: 50 + Math.floor(this.playerScore / 10),
            playerCount: this.playerCount,
        });
        // Save preferences
        this.authManager.savePreference('lastMode', this.gameMode);
        this.authManager.savePreference('lastDifficulty', this.difficulty);
        // Track game results in profile
        const perfectGame = this.aiScore === 0;
        this.profileManager.updateStats({
            won: true,
            score: this.playerScore,
            mode: this.gameMode,
            tilesPlayed: this.movesCount,
            gameTime: this.timeElapsed,
            perfectGame: perfectGame
        });
        // Add XP for winning
        let xpEarned = 50; // Base XP for winning
        xpEarned += Math.floor(this.playerScore / 10); // Bonus based on score
        if (perfectGame)
            xpEarned += 100; // Perfect game bonus
        if (this.difficulty === 'hard')
            xpEarned *= 1.5;
        if (this.difficulty === 'expert')
            xpEarned *= 2;
        this.profileManager.addXP(Math.floor(xpEarned));
        // Update Six-Love streak
        if (this.gameMode === 'sixlove') {
            this.sixLoveStreak.team1++;
            this.sixLoveStreak.team2 = 0;
        }
        // Update achievements
        this.unlockAchievement('first-win');
        if (this.timeElapsed < 120) {
            this.unlockAchievement('speed-demon');
        }
        if (this.aiScore === 0) {
            this.unlockAchievement('perfect-game');
        }
        if (this.maxCombo >= 5) {
            this.unlockAchievement('combo-master');
        }
        // Celebration confetti only for player wins
        for (let i = 0; i < 100; i++) {
            setTimeout(() => {
                this.createParticles(Math.random() * window.innerWidth, Math.random() * window.innerHeight / 2, 'star', 5);
            }, i * 20);
        }
        // Show win screen
        this.showWinScreen(blocked);
    }
    aiWins(blocked = false) {
        this.gameStarted = false;
        // Record match
        this.authManager.recordMatch({
            variant: this.gameMode,
            difficulty: this.difficulty,
            playerScore: this.playerScore,
            opponentScore: this.aiScore,
            won: false,
            blocked,
            moveCount: this.movesCount,
            duration: Math.floor(this.timeElapsed),
            xpEarned: 10,
            playerCount: this.playerCount,
        });
        this.authManager.savePreference('lastMode', this.gameMode);
        this.authManager.savePreference('lastDifficulty', this.difficulty);
        // Track game results in profile
        this.profileManager.updateStats({
            won: false,
            score: this.playerScore,
            mode: this.gameMode,
            tilesPlayed: this.movesCount,
            gameTime: this.timeElapsed,
            perfectGame: false
        });
        // Update Six-Love streak
        if (this.gameMode === 'sixlove') {
            this.sixLoveStreak.team2++;
            this.sixLoveStreak.team1 = 0;
        }
        // Add small XP for participating
        let xpEarned = 10; // Participation XP
        xpEarned += Math.floor(this.playerScore / 20); // Small bonus based on score
        this.profileManager.addXP(Math.floor(xpEarned));
        // Show lose screen
        this.showLoseScreen(blocked);
    }
    gameDraw() {
        this.gameStarted = false;
        // Track as a draw for stats
        this.profileManager.updateStats({
            won: false,
            score: this.playerScore,
            mode: this.gameMode,
            tilesPlayed: this.movesCount,
            gameTime: this.timeElapsed,
            perfectGame: false
        });
        this.profileManager.addXP(25);
        // Record match
        this.authManager.recordMatch({
            variant: this.gameMode,
            difficulty: this.difficulty,
            playerScore: this.playerScore,
            opponentScore: this.aiScore,
            won: false,
            blocked: true,
            moveCount: this.movesCount,
            duration: Math.floor(this.timeElapsed),
            xpEarned: 25,
            playerCount: this.playerCount,
        });
        const rules = getGameRules(this.gameMode);
        const playerPips = this.playerHand.reduce((sum, tile) => sum + tile.left + tile.right, 0);
        const aiPips = this.aiHand.reduce((sum, tile) => sum + tile.left + tile.right, 0);
        const screen = document.createElement('div');
        screen.style.cssText = `
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.92);
      z-index: 3000;
    `;
        screen.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #2a2a1a 0%, #1a1a0d 100%);
        border: 2px solid #ccaa00;
        border-radius: 24px;
        padding: 40px;
        max-width: 450px;
        width: 90%;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0,0,0,0.8);
      ">
        <div style="color: rgba(255,255,255,0.6); font-size: 13px; margin-bottom: 10px;">${rules.icon} ${rules.name}</div>
        <h1 style="font-size: 48px; color: #ccaa00; margin: 10px 0;">DRAW</h1>
        <p style="color: rgba(255,255,255,0.7); margin-bottom: 25px;">Game blocked - equal pip count</p>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 25px;">
          <div style="background: rgba(255,255,255,0.05); padding: 15px; border-radius: 12px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 12px;">YOU</div>
            <div style="color: white; font-size: 24px; font-weight: bold;">${this.playerScore}</div>
            <div style="color: rgba(255,255,255,0.4); font-size: 12px;">${playerPips} pips left</div>
          </div>
          <div style="background: rgba(255,255,255,0.05); padding: 15px; border-radius: 12px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 12px;">OPPONENT</div>
            <div style="color: white; font-size: 24px; font-weight: bold;">${this.aiScore}</div>
            <div style="color: rgba(255,255,255,0.4); font-size: 12px;">${aiPips} pips left</div>
          </div>
        </div>

        <div style="background: rgba(204,170,0,0.1); border: 1px solid rgba(204,170,0,0.2); padding: 10px; border-radius: 8px; margin-bottom: 20px;">
          <div style="color: #ccaa00; font-size: 14px;">+25 XP</div>
        </div>

        <button onclick="game.rematch()" class="menu-button">Rematch</button>
        <button onclick="location.reload()" class="menu-button" style="background: rgba(255,255,255,0.15);">Main Menu</button>
      </div>
    `;
        document.body.appendChild(screen);
    }
    showWinScreen(blocked = false) {
        this.showEndScreen(true, blocked);
    }
    showLoseScreen(blocked = false) {
        this.showEndScreen(false, blocked);
    }
    showEndScreen(isWin, blocked) {
        const rules = getGameRules(this.gameMode);
        const profile = this.profileManager.getProfile();
        const level = this.profileManager.getLevel();
        // Calculate XP earned
        let xpEarned = isWin ? 50 : 10;
        xpEarned += Math.floor(this.playerScore / (isWin ? 10 : 20));
        if (isWin && this.aiScore === 0)
            xpEarned += 100;
        if (this.difficulty === 'hard')
            xpEarned = Math.floor(xpEarned * 1.5);
        if (this.difficulty === 'expert')
            xpEarned *= 2;
        // Get pip counts for all players
        const allPips = [
            this.playerHand.reduce((s, t) => s + t.left + t.right, 0),
            this.aiHand.reduce((s, t) => s + t.left + t.right, 0),
            this.ai2Hand.reduce((s, t) => s + t.left + t.right, 0),
            this.ai3Hand.reduce((s, t) => s + t.left + t.right, 0),
        ];
        const minutes = Math.floor(this.timeElapsed / 60);
        const seconds = Math.floor(this.timeElapsed % 60);
        const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        // Determine which achievements were unlocked this game
        const unlockedAchievements = this.achievements.filter(a => a.unlocked);
        const screen = document.createElement('div');
        screen.style.cssText = `
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.92);
      z-index: 3000;
      animation: fadeIn 0.5s ease-out;
    `;
        const accentColor = isWin ? '#ffd700' : '#888';
        const headerText = isWin ? 'VICTORY!' : 'DEFEAT';
        const headerIcon = isWin ? '🏆' : '💀';
        const bgGrad = isWin
            ? 'linear-gradient(135deg, #1a3a20 0%, #0d1f12 100%)'
            : 'linear-gradient(135deg, #2a1a1a 0%, #1a0d0d 100%)';
        screen.innerHTML = `
      <div style="
        background: ${bgGrad};
        border: 2px solid ${accentColor};
        border-radius: 24px;
        padding: 40px;
        max-width: 550px;
        width: 90%;
        max-height: 85vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0,0,0,0.8);
        text-align: center;
      ">
        <!-- Mode Badge -->
        <div style="
          display: inline-block;
          background: rgba(255,255,255,0.1);
          padding: 6px 16px;
          border-radius: 20px;
          color: rgba(255,255,255,0.7);
          font-size: 13px;
          margin-bottom: 15px;
        ">${rules.icon} ${rules.name} | ${this.difficulty.toUpperCase()}</div>

        <!-- Header -->
        <h1 style="font-size: 52px; color: ${accentColor}; margin: 10px 0;">
          ${headerIcon} ${headerText} ${headerIcon}
        </h1>
        <p style="color: rgba(255,255,255,0.8); font-size: 16px; margin-bottom: 25px;">
          ${blocked ? (isWin ? 'Won by pip count!' : 'Lost by pip count') : (isWin ? 'Dominoed!' : 'Opponent dominoed')}
        </p>

        <!-- Score Summary -->
        <div style="
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 25px;
        ">
          <div style="background: rgba(0,255,100,0.1); padding: 15px; border-radius: 12px; border: 1px solid rgba(0,255,100,0.2);">
            <div style="color: rgba(255,255,255,0.6); font-size: 12px;">YOUR SCORE</div>
            <div style="color: #00ff88; font-size: 32px; font-weight: bold;">${this.playerScore}</div>
          </div>
          <div style="background: rgba(255,100,100,0.1); padding: 15px; border-radius: 12px; border: 1px solid rgba(255,100,100,0.2);">
            <div style="color: rgba(255,255,255,0.6); font-size: 12px;">OPPONENT${this.playerCount > 2 ? 'S' : ''}</div>
            <div style="color: #ff6b6b; font-size: 32px; font-weight: bold;">${this.aiScore}</div>
          </div>
        </div>

        <!-- Stats Grid -->
        <div style="
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 20px;
        ">
          <div style="background: rgba(255,255,255,0.05); padding: 10px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">TIME</div>
            <div style="color: white; font-size: 18px; font-weight: bold;">${timeStr}</div>
          </div>
          <div style="background: rgba(255,255,255,0.05); padding: 10px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">MOVES</div>
            <div style="color: white; font-size: 18px; font-weight: bold;">${this.movesCount}</div>
          </div>
          <div style="background: rgba(255,255,255,0.05); padding: 10px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">COMBO</div>
            <div style="color: white; font-size: 18px; font-weight: bold;">${this.maxCombo}x</div>
          </div>
        </div>

        ${blocked ? `
          <div style="background: rgba(255,255,255,0.05); padding: 12px; border-radius: 8px; margin-bottom: 20px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 12px; margin-bottom: 5px;">REMAINING PIPS</div>
            <div style="color: white; font-size: 14px;">
              You: ${allPips[0]}${this.playerCount >= 3 ? ` | ${this.aiNames[0]}: ${allPips[1]}` : ` | AI: ${allPips[1]}`}
              ${this.playerCount >= 3 ? ` | ${this.aiNames[1]}: ${allPips[2]}` : ''}
              ${this.playerCount >= 4 ? ` | ${this.aiNames[2]}: ${allPips[3]}` : ''}
            </div>
          </div>
        ` : ''}

        <!-- XP Earned -->
        <div style="
          background: linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,215,0,0.05));
          border: 1px solid rgba(255,215,0,0.3);
          padding: 15px;
          border-radius: 12px;
          margin-bottom: 20px;
        ">
          <div style="color: #ffd700; font-size: 14px; font-weight: bold;">XP EARNED</div>
          <div style="color: #ffd700; font-size: 36px; font-weight: bold;">+${xpEarned}</div>
          <div style="color: rgba(255,255,255,0.5); font-size: 12px;">Level ${level}</div>
        </div>

        ${unlockedAchievements.length > 0 ? `
          <div style="margin-bottom: 20px;">
            <div style="color: rgba(255,255,255,0.5); font-size: 12px; margin-bottom: 8px;">ACHIEVEMENTS UNLOCKED</div>
            <div style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: center;">
              ${unlockedAchievements.map(a => `
                <div style="
                  background: rgba(255,215,0,0.15);
                  border: 1px solid rgba(255,215,0,0.3);
                  padding: 6px 12px;
                  border-radius: 20px;
                  color: #ffd700;
                  font-size: 13px;
                ">${a.icon} ${a.title}</div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <!-- Action Buttons -->
        <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
          <button onclick="game.rematch()" style="
            padding: 14px 32px;
            background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
            border: none; color: white; font-size: 16px; font-weight: bold;
            border-radius: 12px; cursor: pointer;
            transition: transform 0.2s;
            box-shadow: 0 4px 15px rgba(76,175,80,0.4);
          " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
            Rematch
          </button>
          <button onclick="location.reload()" style="
            padding: 14px 32px;
            background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.2);
            color: white; font-size: 16px;
            border-radius: 12px; cursor: pointer;
            transition: transform 0.2s;
          " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
            Main Menu
          </button>
        </div>
      </div>
    `;
        document.body.appendChild(screen);
    }
    usePowerUp(powerUp) {
        if (!powerUp.active)
            return;
        switch (powerUp.type) {
            case 'hint':
                this.showHintPowerUp();
                break;
            case 'undo':
                this.undoLastMove();
                break;
            case 'peek':
                this.peekAIHand();
                break;
            case 'double-score':
                this.activateDoubleScore();
                break;
        }
        powerUp.active = false;
        powerUp.cooldown = 15; // 15 second cooldown (reduced from 30)
        this.playSound('powerup');
        // Small visual feedback without particles
        this.screenShake = 2;
    }
    showHintPowerUp() {
        this.hints = [];
        for (const tile of this.playerHand) {
            if (this.canPlayOnSide(tile, 'left')) {
                this.hints.push({ tile, side: 'left' });
                this.showMessage('Hint: Green glowing tile can be played on the left!', 5000);
                break;
            }
            if (this.canPlayOnSide(tile, 'right')) {
                this.hints.push({ tile, side: 'right' });
                this.showMessage('Hint: Green glowing tile can be played on the right!', 5000);
                break;
            }
        }
        if (this.hints.length === 0) {
            this.showMessage('No valid moves! You must pass.', 3000);
        }
        this.showHint = true;
        setTimeout(() => this.showHint = false, 7000);
    }
    undoLastMove() {
        // Undo not implemented yet
        this.showMessage('Undo feature coming soon!', 2000);
    }
    peekAIHand() {
        // Show AI's tile count prominently
        this.showMessage(`AI has ${this.aiHand.length} tiles remaining!`, 3000);
    }
    activateDoubleScore() {
        // Double scoring message
        this.showMessage('2X Score activated for next 3 moves! (All Fives mode)', 4000);
    }
    toggleHints() {
        this.showHint = !this.showHint;
    }
    toggleSound() {
        this.soundEnabled = !this.soundEnabled;
    }
    showAchievements() {
        const existing = document.getElementById('achievementsScreen');
        if (existing)
            existing.remove();
        const screen = document.createElement('div');
        screen.id = 'achievementsScreen';
        screen.style.cssText = `
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.92);
      z-index: 4000;
    `;
        const profile = this.profileManager.getProfile();
        const allAchievements = profile.achievements || [];
        screen.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%);
        border: 2px solid #ffd700;
        border-radius: 24px;
        padding: 35px;
        max-width: 600px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0,0,0,0.8);
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px;">
          <h2 style="color: #ffd700; font-size: 28px; margin: 0;">Achievements</h2>
          <button id="closeAchievements" style="
            background: rgba(255,255,255,0.1); border: none; color: white;
            width: 36px; height: 36px; border-radius: 50%; font-size: 18px; cursor: pointer;
          ">✕</button>
        </div>

        <div style="color: rgba(255,255,255,0.5); font-size: 14px; margin-bottom: 20px;">
          ${allAchievements.filter((a) => a.unlocked).length} / ${allAchievements.length} unlocked
        </div>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          ${allAchievements.map((a) => `
            <div style="
              display: flex; align-items: center; gap: 15px;
              padding: 15px;
              background: ${a.unlocked ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)'};
              border: 1px solid ${a.unlocked ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.08)'};
              border-radius: 12px;
              opacity: ${a.unlocked ? '1' : '0.5'};
            ">
              <div style="font-size: 32px; min-width: 40px; text-align: center;">${a.icon}</div>
              <div style="flex: 1;">
                <div style="color: ${a.unlocked ? '#ffd700' : 'white'}; font-weight: bold; font-size: 15px;">${a.name}</div>
                <div style="color: rgba(255,255,255,0.5); font-size: 13px;">${a.description}</div>
                ${!a.unlocked && a.progress !== undefined ? `
                  <div style="
                    margin-top: 6px; height: 4px; background: rgba(255,255,255,0.1);
                    border-radius: 2px; overflow: hidden;
                  ">
                    <div style="height: 100%; width: ${Math.min(100, (a.progress / a.maxProgress) * 100)}%; background: #ffd700; border-radius: 2px;"></div>
                  </div>
                ` : ''}
              </div>
              ${a.unlocked ? '<div style="color: #ffd700; font-size: 13px;">✓</div>' : ''}
            </div>
          `).join('')}

          ${this.achievements.filter(a => !allAchievements.some((pa) => pa.id === a.id)).map(a => `
            <div style="
              display: flex; align-items: center; gap: 15px;
              padding: 15px;
              background: ${a.unlocked ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)'};
              border: 1px solid ${a.unlocked ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.08)'};
              border-radius: 12px;
              opacity: ${a.unlocked ? '1' : '0.5'};
            ">
              <div style="font-size: 32px; min-width: 40px; text-align: center;">${a.icon}</div>
              <div style="flex: 1;">
                <div style="color: ${a.unlocked ? '#ffd700' : 'white'}; font-weight: bold; font-size: 15px;">${a.title}</div>
                <div style="color: rgba(255,255,255,0.5); font-size: 13px;">${a.description}</div>
                <div style="
                  margin-top: 6px; height: 4px; background: rgba(255,255,255,0.1);
                  border-radius: 2px; overflow: hidden;
                ">
                  <div style="height: 100%; width: ${Math.min(100, (a.progress / a.maxProgress) * 100)}%; background: #ffd700; border-radius: 2px;"></div>
                </div>
              </div>
              ${a.unlocked ? '<div style="color: #ffd700; font-size: 13px;">✓</div>' : ''}
            </div>
          `).join('')}
        </div>
      </div>
    `;
        document.body.appendChild(screen);
        document.getElementById('closeAchievements')?.addEventListener('click', () => {
            this.playSound('click');
            screen.remove();
        });
        screen.addEventListener('click', (e) => {
            if (e.target === screen)
                screen.remove();
        });
    }
    showStats() {
        const existing = document.getElementById('statsScreen');
        if (existing)
            existing.remove();
        const profile = this.profileManager.getProfile();
        const stats = profile.stats;
        const winRate = stats.gamesPlayed > 0 ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100) : 0;
        const screen = document.createElement('div');
        screen.id = 'statsScreen';
        screen.style.cssText = `
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.92);
      z-index: 4000;
    `;
        // Build mode stats if available
        const modeStats = stats.modeStats || {};
        const modeRows = Object.entries(modeStats).map(([mode, ms]) => {
            const modeRules = getGameRules(mode);
            const modeWinRate = ms.played > 0 ? Math.round((ms.won / ms.played) * 100) : 0;
            return `
        <div style="
          display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 10px; padding: 10px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          color: white; font-size: 14px;
        ">
          <div>${modeRules.icon} ${modeRules.name}</div>
          <div style="text-align: center;">${ms.played || 0}</div>
          <div style="text-align: center;">${ms.won || 0}</div>
          <div style="text-align: center; color: ${modeWinRate >= 50 ? '#00ff88' : '#ff6b6b'};">${modeWinRate}%</div>
        </div>
      `;
        }).join('');
        screen.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%);
        border: 2px solid #00ccff;
        border-radius: 24px;
        padding: 35px;
        max-width: 600px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0,0,0,0.8);
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px;">
          <h2 style="color: #00ccff; font-size: 28px; margin: 0;">Player Stats</h2>
          <button id="closeStats" style="
            background: rgba(255,255,255,0.1); border: none; color: white;
            width: 36px; height: 36px; border-radius: 50%; font-size: 18px; cursor: pointer;
          ">✕</button>
        </div>

        <!-- Overview Cards -->
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 25px;">
          <div style="background: rgba(255,255,255,0.05); padding: 15px; border-radius: 12px; text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">PLAYED</div>
            <div style="color: white; font-size: 24px; font-weight: bold;">${stats.gamesPlayed}</div>
          </div>
          <div style="background: rgba(255,255,255,0.05); padding: 15px; border-radius: 12px; text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">WON</div>
            <div style="color: #00ff88; font-size: 24px; font-weight: bold;">${stats.gamesWon}</div>
          </div>
          <div style="background: rgba(255,255,255,0.05); padding: 15px; border-radius: 12px; text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">WIN RATE</div>
            <div style="color: ${winRate >= 50 ? '#00ff88' : '#ff6b6b'}; font-size: 24px; font-weight: bold;">${winRate}%</div>
          </div>
          <div style="background: rgba(255,255,255,0.05); padding: 15px; border-radius: 12px; text-align: center;">
            <div style="color: rgba(255,255,255,0.5); font-size: 11px;">STREAK</div>
            <div style="color: #ffd700; font-size: 24px; font-weight: bold;">${stats.winStreak || 0}</div>
          </div>
        </div>

        <!-- Detailed Stats -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 25px;">
          <div style="background: rgba(255,255,255,0.03); padding: 12px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.4); font-size: 11px;">BEST STREAK</div>
            <div style="color: white; font-size: 18px;">${stats.bestWinStreak || 0}</div>
          </div>
          <div style="background: rgba(255,255,255,0.03); padding: 12px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.4); font-size: 11px;">HIGHEST SCORE</div>
            <div style="color: white; font-size: 18px;">${stats.highestScore || 0}</div>
          </div>
          <div style="background: rgba(255,255,255,0.03); padding: 12px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.4); font-size: 11px;">TOTAL TILES</div>
            <div style="color: white; font-size: 18px;">${stats.tilesPlayed || 0}</div>
          </div>
          <div style="background: rgba(255,255,255,0.03); padding: 12px; border-radius: 8px;">
            <div style="color: rgba(255,255,255,0.4); font-size: 11px;">PLAY TIME</div>
            <div style="color: white; font-size: 18px;">${Math.floor((stats.totalPlayTime || 0) / 60)}m</div>
          </div>
        </div>

        ${modeRows ? `
          <h3 style="color: rgba(255,255,255,0.7); font-size: 16px; margin-bottom: 10px;">By Game Mode</h3>
          <div style="background: rgba(255,255,255,0.03); border-radius: 12px; padding: 10px 15px;">
            <div style="
              display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
              gap: 10px; padding-bottom: 8px;
              border-bottom: 1px solid rgba(255,255,255,0.1);
              color: rgba(255,255,255,0.5); font-size: 12px;
            ">
              <div>Mode</div>
              <div style="text-align: center;">Played</div>
              <div style="text-align: center;">Won</div>
              <div style="text-align: center;">Rate</div>
            </div>
            ${modeRows}
          </div>
        ` : ''}

        <!-- Level Progress -->
        <div style="margin-top: 20px; background: rgba(255,255,255,0.03); padding: 15px; border-radius: 12px;">
          <div style="color: rgba(255,255,255,0.5); font-size: 12px; margin-bottom: 5px;">
            Level ${this.profileManager.getLevel()}
          </div>
          <div style="height: 8px; background: rgba(255,255,255,0.1); border-radius: 4px; overflow: hidden;">
            <div style="
              height: 100%;
              width: ${((profile.xp % 100) / 100) * 100}%;
              background: linear-gradient(90deg, #00ccff, #00ff88);
              border-radius: 4px;
            "></div>
          </div>
          <div style="color: rgba(255,255,255,0.4); font-size: 11px; margin-top: 4px;">
            ${profile.xp % 100} / 100 XP to next level
          </div>
        </div>
      </div>
    `;
        document.body.appendChild(screen);
        document.getElementById('closeStats')?.addEventListener('click', () => {
            this.playSound('click');
            screen.remove();
        });
        screen.addEventListener('click', (e) => {
            if (e.target === screen)
                screen.remove();
        });
    }
    showAuthModal() {
        const existing = document.getElementById('authModal');
        if (existing)
            existing.remove();
        const user = this.authManager.getUser();
        const isLoggedIn = user && !user.isGuest;
        const modal = document.createElement('div');
        modal.id = 'authModal';
        modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.92); z-index: 5000;
    `;
        if (isLoggedIn) {
            // Logged in view
            modal.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #1a1a2e, #0f0f1e);
          border: 2px solid #667eea; border-radius: 24px;
          padding: 40px; max-width: 400px; width: 90%; text-align: center;
          box-shadow: 0 20px 60px rgba(0,0,0,0.8);
        ">
          <h2 style="color: white; margin-bottom: 20px;">Signed In</h2>
          <p style="color: rgba(255,255,255,0.7); margin-bottom: 5px;">${user.email}</p>
          <p style="color: #667eea; font-size: 18px; font-weight: bold; margin-bottom: 25px;">${user.username}</p>

          <div style="display: flex; gap: 10px; justify-content: center;">
            <button id="authLogout" style="
              padding: 12px 24px; background: rgba(255,100,100,0.2);
              border: 1px solid rgba(255,100,100,0.3); color: #ff6b6b;
              border-radius: 12px; cursor: pointer; font-size: 14px;
            ">Sign Out</button>
            <button id="authClose" style="
              padding: 12px 24px; background: rgba(255,255,255,0.1);
              border: 1px solid rgba(255,255,255,0.2); color: white;
              border-radius: 12px; cursor: pointer; font-size: 14px;
            ">Close</button>
          </div>
        </div>
      `;
            document.body.appendChild(modal);
            document.getElementById('authLogout')?.addEventListener('click', async () => {
                await this.authManager.logout();
                modal.remove();
                location.reload();
            });
            document.getElementById('authClose')?.addEventListener('click', () => modal.remove());
        }
        else {
            // Login/Register view
            modal.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #1a1a2e, #0f0f1e);
          border: 2px solid #667eea; border-radius: 24px;
          padding: 40px; max-width: 400px; width: 90%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.8);
        ">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px;">
            <h2 style="color: white; margin: 0;">Sign In</h2>
            <button id="authClose2" style="
              background: rgba(255,255,255,0.1); border: none; color: white;
              width: 36px; height: 36px; border-radius: 50%; font-size: 18px; cursor: pointer;
            ">✕</button>
          </div>

          <div id="authError" style="
            display: none; background: rgba(255,50,50,0.15);
            border: 1px solid rgba(255,50,50,0.3); color: #ff6b6b;
            padding: 10px; border-radius: 8px; margin-bottom: 15px; font-size: 13px;
          "></div>

          <div style="display: flex; flex-direction: column; gap: 12px;">
            <input id="authEmail" type="email" placeholder="Email" autocomplete="email" style="
              padding: 14px; background: rgba(255,255,255,0.05);
              border: 1px solid rgba(255,255,255,0.15); color: white;
              border-radius: 10px; font-size: 15px; outline: none;
            " />
            <input id="authPassword" type="password" placeholder="Password" autocomplete="current-password" style="
              padding: 14px; background: rgba(255,255,255,0.05);
              border: 1px solid rgba(255,255,255,0.15); color: white;
              border-radius: 10px; font-size: 15px; outline: none;
            " />
            <input id="authUsername" type="text" placeholder="Username (for new accounts)" autocomplete="username" style="
              padding: 14px; background: rgba(255,255,255,0.05);
              border: 1px solid rgba(255,255,255,0.15); color: white;
              border-radius: 10px; font-size: 15px; outline: none;
            " />

            <button id="authLoginBtn" style="
              padding: 14px; background: linear-gradient(135deg, #667eea, #764ba2);
              border: none; color: white; font-size: 16px; font-weight: bold;
              border-radius: 10px; cursor: pointer;
            ">Sign In</button>

            <button id="authRegisterBtn" style="
              padding: 14px; background: rgba(255,255,255,0.1);
              border: 1px solid rgba(255,255,255,0.2); color: white;
              font-size: 14px; border-radius: 10px; cursor: pointer;
            ">Create Account</button>
          </div>

          <p style="color: rgba(255,255,255,0.4); font-size: 12px; margin-top: 15px; text-align: center;">
            Playing as guest? Your stats are saved locally.
          </p>
        </div>
      `;
            document.body.appendChild(modal);
            const showError = (msg) => {
                const el = document.getElementById('authError');
                if (el) {
                    el.textContent = msg;
                    el.style.display = 'block';
                }
            };
            document.getElementById('authLoginBtn')?.addEventListener('click', async () => {
                const email = document.getElementById('authEmail').value;
                const password = document.getElementById('authPassword').value;
                if (!email || !password) {
                    showError('Email and password required');
                    return;
                }
                try {
                    await this.authManager.login(email, password);
                    modal.remove();
                    location.reload();
                }
                catch (e) {
                    showError(e.message);
                }
            });
            document.getElementById('authRegisterBtn')?.addEventListener('click', async () => {
                const email = document.getElementById('authEmail').value;
                const password = document.getElementById('authPassword').value;
                const username = document.getElementById('authUsername').value;
                if (!email || !password) {
                    showError('Email and password required');
                    return;
                }
                try {
                    await this.authManager.register(email, password, username || email.split('@')[0]);
                    modal.remove();
                    location.reload();
                }
                catch (e) {
                    showError(e.message);
                }
            });
            document.getElementById('authClose2')?.addEventListener('click', () => modal.remove());
        }
        modal.addEventListener('click', (e) => { if (e.target === modal)
            modal.remove(); });
    }
    showMatchHistory() {
        const existing = document.getElementById('matchHistoryScreen');
        if (existing)
            existing.remove();
        const matches = this.authManager.getMatchHistory();
        const screen = document.createElement('div');
        screen.id = 'matchHistoryScreen';
        screen.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.92); z-index: 4000;
    `;
        const matchRows = matches.slice(0, 20).map(m => {
            const rules = getGameRules(m.variant);
            const date = new Date(m.timestamp);
            const timeStr = `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
            return `
        <div style="
          display: grid; grid-template-columns: auto 2fr 1fr 1fr 1fr;
          gap: 12px; align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          font-size: 14px;
        ">
          <div style="font-size: 20px;">${m.won ? '🏆' : '💀'}</div>
          <div>
            <div style="color: white;">${rules.icon} ${rules.name}</div>
            <div style="color: rgba(255,255,255,0.4); font-size: 11px;">${timeStr} | ${m.difficulty}</div>
          </div>
          <div style="color: ${m.won ? '#00ff88' : '#ff6b6b'}; text-align: center;">
            ${m.playerScore}-${m.opponentScore}
          </div>
          <div style="color: rgba(255,255,255,0.5); text-align: center; font-size: 12px;">
            ${Math.floor(m.duration / 60)}:${(m.duration % 60).toString().padStart(2, '0')}
          </div>
          <div style="color: #ffd700; text-align: center; font-size: 12px;">
            +${m.xpEarned} XP
          </div>
        </div>
      `;
        }).join('');
        screen.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #1a1a2e, #0f0f1e);
        border: 2px solid #888;
        border-radius: 24px;
        padding: 35px;
        max-width: 650px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0,0,0,0.8);
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h2 style="color: white; margin: 0;">Match History</h2>
          <button id="closeHistory" style="
            background: rgba(255,255,255,0.1); border: none; color: white;
            width: 36px; height: 36px; border-radius: 50%; font-size: 18px; cursor: pointer;
          ">✕</button>
        </div>

        ${matches.length === 0
            ? '<p style="color: rgba(255,255,255,0.5); text-align: center; padding: 40px;">No matches played yet. Start a game!</p>'
            : matchRows}
      </div>
    `;
        document.body.appendChild(screen);
        document.getElementById('closeHistory')?.addEventListener('click', () => {
            this.playSound('click');
            screen.remove();
        });
        screen.addEventListener('click', (e) => { if (e.target === screen)
            screen.remove(); });
    }
    unlockAchievement(id) {
        const achievement = this.achievements.find(a => a.id === id);
        if (achievement && !achievement.unlocked) {
            achievement.unlocked = true;
            this.showAchievementToast(achievement);
        }
    }
    showAchievementToast(achievement) {
        const toast = document.createElement('div');
        toast.className = 'achievement-toast';
        toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 15px;">
        <span style="font-size: 32px;">${achievement.icon}</span>
        <div>
          <div style="font-weight: bold; font-size: 18px;">${achievement.title}</div>
          <div style="font-size: 14px; opacity: 0.9;">${achievement.description}</div>
        </div>
      </div>
    `;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 4000);
    }
}
// Initialize game when ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new AwesomeDominoGame();
    });
}
else {
    new AwesomeDominoGame();
}
